import { useState, useRef, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Banknote, QrCode, CreditCard, Check, CheckCircle2, Loader2, Printer, X, AlertTriangle,
  Eye, RotateCcw, Plus, Pencil, Trash2, FileSpreadsheet, Upload, ChevronDown, Users,
  ChevronLeft, ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDate } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { useFilial } from '@/lib/filial'
import { useMarketplaceAtivo } from '@/hooks/useModulo'
import { pedirExclusao } from '@/lib/confirmarExclusao'

const FORMAS = [
  { id: 'dinheiro', label: 'Dinheiro', icon: Banknote },
  { id: 'pix', label: 'PIX', icon: QrCode },
  { id: 'cartao_debito', label: 'Debito', icon: CreditCard },
  { id: 'cartao_credito', label: 'Credito', icon: CreditCard },
  { id: 'transferencia', label: 'Transf.', icon: Banknote },
]

function imprimirRecibo(conta: any, pagamento: any) {
  const html =
    '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Recibo</title><style>@media print{@page{margin:0;size:80mm auto;}body{margin:0;}}body{font-family:"Courier New",monospace;font-size:13px;font-weight:bold;width:280px;margin:0 auto;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact;}*{color:#000!important;}.center{text-align:center;}.line{border-top:1px dashed #000;margin:6px 0;}.big{font-size:16px;font-weight:bold;}</style></head><body><div class="center"><img src="/logo-empresa.jpeg" style="width:90px;height:auto;margin:6px auto 2px;display:block;" /><div style="font-size:16px;font-weight:bold;margin:8px 0 2px;">VELORE ERP</div><div style="font-size:10px;margin-bottom:6px;">RECIBO DE PAGAMENTO</div></div><div class="line"></div><div style="font-size:11px;">Ref: ' +
    conta.descricao +
    '</div><div style="font-size:11px;">Data: ' +
    pagamento.data +
    '</div><div class="line"></div><div style="display:flex;justify-content:space-between;"><span>Valor pago:</span><span class="big">' +
    formatMoney(pagamento.valor) +
    '</span></div><div style="display:flex;justify-content:space-between;margin-top:4px;"><span>Forma:</span><span>' +
    pagamento.forma.replace('_', ' ').toUpperCase() +
    '</span></div><div class="line"></div><div class="center" style="font-size:10px;margin-top:8px;">VELORE ERP - app.velore-erp.com.br</div><script>window.onload=function(){window.print();}</script></body></html>'
  const win = window.open('', '_blank', 'width=320,height=500')
  if (win) { win.document.write(html); win.document.close() }
}

function imprimirRelatorio(contas: any[], titulo: string, filtros: string) {
  const linhas = contas
    .map(
      (c) =>
        '<tr><td>' + c.descricao + '</td><td>' + (c.cliente_nome || '-') + '</td><td>' +
        formatDate(c.vencimento) + '</td><td style="text-align:right">' + formatMoney(c.valor) +
        '</td><td style="text-align:right">' + formatMoney(c.valor_pago) + '</td><td>' +
        (c.forma_pagamento || '-') + '</td><td>' + c.status + '</td></tr>',
    )
    .join('')
  const totalVal = contas.reduce((s, c) => s + Number(c.valor), 0)
  const totalPago = contas.reduce((s, c) => s + Number(c.valor_pago), 0)
  const html =
    '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + titulo +
    '</title><style>body{font-family:Arial,sans-serif;font-size:11px;margin:20px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;}th{background:#f0f0f0;font-size:10px;}h2{margin:0 0 4px;}@media print{@page{margin:10mm;}}</style></head><body><img src="/logo-empresa.jpeg" style="height:48px;width:auto;margin-bottom:8px;" /><h2>VELORE ERP - ' +
    titulo + '</h2><p style="font-size:10px;color:#666;">' + filtros +
    ' | Gerado em: ' + new Date().toLocaleString('pt-BR') +
    '</p><table><thead><tr><th>Descricao</th><th>Cliente</th><th>Vencimento</th><th style="text-align:right">Valor</th><th style="text-align:right">Pago</th><th>Forma</th><th>Status</th></tr></thead><tbody>' +
    linhas +
    '</tbody><tfoot><tr style="font-weight:bold;"><td colspan="3">TOTAL (' + contas.length +
    ' titulos)</td><td style="text-align:right">' + formatMoney(totalVal) +
    '</td><td style="text-align:right">' + formatMoney(totalPago) +
    '</td><td colspan="2">Saldo: ' + formatMoney(totalVal - totalPago) +
    '</td></tr></tfoot></table><script>window.onload=function(){window.print();}</script></body></html>'
  const win = window.open('', '_blank', 'width=900,height=600')
  if (win) { win.document.write(html); win.document.close() }
}

// Soma k meses mantendo o DIA do mês (clampa pro último dia quando o mês não tiver).
// Ex.: dia 10 → 10 do mês seguinte; 31/jan → 28/fev. Mais inteligente que +30 dias.
function addMesesMantendoDia(baseISO: string, k: number): string {
  const d = new Date(baseISO + 'T00:00:00')
  const dia = d.getDate()
  const nd = new Date(d.getFullYear(), d.getMonth() + k, 1)
  const ultimo = new Date(nd.getFullYear(), nd.getMonth() + 1, 0).getDate()
  nd.setDate(Math.min(dia, ultimo))
  const mm = String(nd.getMonth() + 1).padStart(2, '0')
  const dd = String(nd.getDate()).padStart(2, '0')
  return `${nd.getFullYear()}-${mm}-${dd}`
}

export default function FinanceiroPage({ tipo }: { tipo: 'receber' | 'pagar' }) {
  const qc = useQueryClient()
  const marketplaceAtivo = useMarketplaceAtivo()
  const [searchParams] = useSearchParams()
  const qUrl = searchParams.get('q') || ''
    // `?status=` (vazio) significa TODOS — diferente de ausente, que mantém o
  // padrão "em aberto". Por isso o has() em vez de só o get().
  const temStatusUrl = searchParams.has('status')
  const statusUrl = searchParams.get('status') || ''
  const [status, setStatus] = useState(temStatusUrl ? statusUrl : 'em_aberto')
  const [formaPgFiltro, setFormaPgFiltro] = useState('')
  const [periodoPreset, setPeriodoPreset] = useState<'hoje'|'7d'|'30d'|'mes'|'mes_ant'|'todos'|'custom'>(
    qUrl ? 'todos' : 'mes')
  const [periodoIni, setPeriodoIni] = useState<string>('')
  const [periodoFim, setPeriodoFim] = useState<string>('')
  const [origemFiltro, setOrigemFiltro] = useState<string>('')  // venda | tiny | manual | compra
  const [cgFiltro, setCgFiltro] = useState<string>('')  // conta_gerencial_id
  const [vendedorFiltro, setVendedorFiltro] = useState<string>('')  // vendedor_id
  const [busca, setBusca] = useState<string>(qUrl)
  const [valorMin, setValorMin] = useState<string>('')
  const [valorMax, setValorMax] = useState<string>('')
  const [importModal, setImportModal] = useState(false)
  const [baixaModal, setBaixaModal] = useState<any>(null)
  const [detalheModal, setDetalheModal] = useState<any>(null)
  const [novoModal, setNovoModal] = useState(false)
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [baixaLoteModal, setBaixaLoteModal] = useState(false)
  const [borderoModal, setBorderoModal] = useState(false)
  const [alterarCgModal, setAlterarCgModal] = useState(false)
  const [novaCg, setNovaCg] = useState<string>('')
  const [transferirRcaModal, setTransferirRcaModal] = useState(false)
  const [novoRca, setNovoRca] = useState<string>('')
  const [editarModal, setEditarModal] = useState<any>(null)
  const [baixaForm, setBaixaForm] = useState({ valor: '', forma: 'dinheiro', obs: '', conta_id: '' as '' | number, data: '', juros: '', juros_pct: '' })
  // Juros efetivo (R$) = % sobre o valor a pagar + juros R$ manual
  const jurosCalc = (() => {
    const v = parseFloat(baixaForm.valor || '0') || 0
    const pct = parseFloat(baixaForm.juros_pct || '0') || 0
    const fixo = parseFloat(baixaForm.juros || '0') || 0
    return Math.round((v * pct / 100 + fixo) * 100) / 100
  })()
  const [ultimaBaixa, setUltimaBaixa] = useState<any>(null)

  const hoje = new Date().toISOString().split('T')[0]
  const EMPTY_NOVO = {
    tipo,
    descricao: '',
    pessoa_id: '' as '' | number,
    conta_gerencial_id: '' as '' | number,
    filial_id: '' as '' | number,  // '' = usa a filial atual (header); admin pode escolher
    valor: '',
    vencimento: hoje,
    data_competencia: '',
    total_parcelas: 1,
    forma_pagamento: '',
    juros_pct: '2',
    multa_valor: '',
    juros_valor: '',
    cheque_banco: '',
    cheque_agencia: '',
    cheque_numero: '',
    cheque_bom_para: '',
    observacao: '',
  }
  const [novoForm, setNovoForm] = useState(EMPTY_NOVO)
  // Parcelas editáveis (vencimento+valor por parcela). Regenera ao mudar valor/vencimento/N
  // (default mensal mantendo o dia); o usuário pode ajustar vencimentos/valores específicos.
  const [parcelasCustom, setParcelasCustom] = useState<{ vencimento: string; valor: string }[]>([])
  useEffect(() => {
    const n = Math.max(1, Math.floor(Number(novoForm.total_parcelas) || 1))
    if (n < 2 || !novoForm.vencimento) { setParcelasCustom([]); return }
    // DIVIDE o valor total entre as N parcelas (centavos da sobra vão na 1ª).
    const totalCent = Math.round((parseFloat(novoForm.valor) || 0) * 100)
    const baseCent = Math.floor(totalCent / n)
    const sobra = totalCent - baseCent * n
    setParcelasCustom(Array.from({ length: n }, (_, k) => ({
      vencimento: addMesesMantendoDia(novoForm.vencimento, k),
      valor: ((baseCent + (k === 0 ? sobra : 0)) / 100).toFixed(2),
    })))
  }, [novoForm.valor, novoForm.vencimento, novoForm.total_parcelas])
  // aplica o DIA do vencimento desta parcela em todas as seguintes (mantendo os meses)
  const aplicarDiaAbaixo = (idx: number) => setParcelasCustom((arr) => {
    const dia = new Date(arr[idx].vencimento + 'T00:00:00').getDate()
    return arr.map((x, i) => {
      if (i <= idx) return x
      const d = new Date(x.vencimento + 'T00:00:00')
      const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
      d.setDate(Math.min(dia, ultimo))
      const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0')
      return { ...x, vencimento: `${d.getFullYear()}-${mm}-${dd}` }
    })
  })

  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const { filialAtual, modoConsolidado, setModoConsolidado } = useFilial()
  // Filiais que o usuário pode ver — popula o dropdown de filial no cadastro e o
  // rótulo na lista. apenas_minhas: admin/gerente recebem todas. (pedido 26/06)
  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-fin'],
    queryFn: async () => (await api.get('/filiais', { params: { apenas_minhas: true } })).data,
    staleTime: 5 * 60_000,
  })
  // Só mostra a coluna Filial quando faz sentido (usuário enxerga >1 filial) —
  // pro admin no consolidado isso identifica a loja de cada lançamento.
  const mostrarFilial = filiais.length > 1
  const { data: contas = [], isLoading } = useQuery<any[]>({
    queryKey: ['financeiro-cli', tipo, status, filialAtualId, modoConsolidado],
    queryFn: async () =>
      (await api.get('/financeiro/com-cliente', { params: { tipo, status: status || undefined } })).data,
  })

  // Busca só o tipo certo (pagar→fornecedor, receber→cliente) com limite alto pra trazer
  // TODOS. Antes era sem tipo + cap 1000 → com 2700+ clientes na frente, os fornecedores
  // ficavam de fora do modal de nova conta a pagar.
  const { data: pessoas = [] } = useQuery<any[]>({
    queryKey: ['pessoas-fin', tipo],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { tipo: tipo === 'receber' ? 'cliente' : 'fornecedor', limit: 5000 },
    })).data,
  })

  const { data: bancos = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias-baixa'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  const tiposCg = tipo === 'receber'
    ? ['receita', 'deducao', 'receita_fin']
    : ['custo', 'despesa_op', 'despesa_fin']

  // 1 chamada só + filtro no cliente. Antes eram 3 chamadas com Promise.all —
  // se UMA falhasse (timeout/hiccup), a lista inteira de contas gerenciais sumia.
  const { data: cgsAll = [] } = useQuery<any[]>({
    queryKey: ['cg-analiticas-all'],
    queryFn: async () => (await api.get('/plano-contas/analiticas')).data,
  })
  const cgs = cgsAll.filter((c: any) => tiposCg.includes(c.tipo))

  // Vendedores únicos presentes nos títulos carregados (pra popular o filtro)
  const vendedoresDisponiveis = useMemo(() => {
    const m = new Map<number, { id: number; codigo: string | null; nome: string }>()
    contas.forEach((c: any) => {
      if (c.vendedor_id && !m.has(c.vendedor_id)) {
        m.set(c.vendedor_id, { id: c.vendedor_id, codigo: c.vendedor_codigo, nome: c.vendedor_nome })
      }
    })
    return Array.from(m.values()).sort((a, b) => {
      const ca = parseInt(a.codigo || '999'), cb = parseInt(b.codigo || '999')
      return ca - cb
    })
  }, [contas])

  // Calcula janela de datas com base no preset
  const { dtIni, dtFim } = (() => {
    const h = new Date()
    h.setHours(0,0,0,0)
    const fmt = (d: Date) => d.toISOString().slice(0,10)
    if (periodoPreset === 'todos') return { dtIni: '', dtFim: '' }
    if (periodoPreset === 'custom') return { dtIni: periodoIni, dtFim: periodoFim }
    if (periodoPreset === 'hoje') return { dtIni: fmt(h), dtFim: fmt(h) }
    if (periodoPreset === '7d') {
      const ini = new Date(h); ini.setDate(ini.getDate()-7); return { dtIni: fmt(ini), dtFim: fmt(h) }
    }
    if (periodoPreset === '30d') {
      const ini = new Date(h); ini.setDate(ini.getDate()-30); return { dtIni: fmt(ini), dtFim: fmt(h) }
    }
    if (periodoPreset === 'mes') {
      const ini = new Date(h.getFullYear(), h.getMonth(), 1)
      const fim = new Date(h.getFullYear(), h.getMonth()+1, 0)
      return { dtIni: fmt(ini), dtFim: fmt(fim) }
    }
    if (periodoPreset === 'mes_ant') {
      const ini = new Date(h.getFullYear(), h.getMonth()-1, 1)
      const fim = new Date(h.getFullYear(), h.getMonth(), 0)
      return { dtIni: fmt(ini), dtFim: fmt(fim) }
    }
    return { dtIni: '', dtFim: '' }
  })()

  const contasFiltradas = contas.filter((c: any) => {
    if (formaPgFiltro && c.forma_pagamento !== formaPgFiltro) return false
    if (origemFiltro && c.origem !== origemFiltro) return false
    if (cgFiltro && String(c.conta_gerencial_id || '') !== cgFiltro) return false
    if (vendedorFiltro && String(c.vendedor_id || '') !== vendedorFiltro) return false
    // Período só filtra quando NÃO há busca de texto — buscar deve varrer tudo
    // (senão um título de outro mês some e parece que a busca "não acha").
    if (!busca.trim()) {
      if (dtIni && c.vencimento < dtIni) return false
      if (dtFim && c.vencimento > dtFim) return false
    }
    if (valorMin && Number(c.valor) < Number(valorMin)) return false
    if (valorMax && Number(c.valor) > Number(valorMax)) return false
    if (busca) {
      const q = busca.toLowerCase().trim()
      const hay = [
        c.descricao, c.cliente_nome, c.cliente_codigo, c.cliente_documento, c.numero_nf,
        c.pedido_numero, c.numero_doc_simples, c.vendedor_codigo, c.vendedor_nome,
        c.ml_order_id, c.mp_payment_id, c.pedido_externo_numero, String(c.id),
      ].filter((x) => x !== null && x !== undefined && x !== '').join(' ').toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })

  // Ordenação por coluna (clique no cabeçalho) — mesmo padrão da tela de Pedidos
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)
  const sortVal = (c: any, key: string) => {
    switch (key) {
      case 'cliente': return (c.cliente_nome || '').toLowerCase()
      case 'filial': return (c.filial_nome || '').toLowerCase()
      case 'pedido': return Number(c.pedido_numero || c.numero_doc_simples || 0)
      case 'nf': return (c.numero_nf || '').toString().toLowerCase()
      case 'ml': return (c.ml_order_id || '').toString()
      case 'mp': return (c.mp_payment_id || '').toString()
      case 'vendedor': return (c.vendedor_nome || '').toLowerCase()
      case 'cg': return (c.conta_gerencial_codigo || '')
      case 'vencimento': return c.vencimento || ''
      case 'parcela': return Number(c.parcela || 0)
      case 'valor': return Number(c.valor || 0)
      case 'recebido': return Number(c.valor_pago || 0)
      case 'juros': return Number(c.juros_valor || 0)
      case 'forma': return (c.forma_pagamento || '').toLowerCase()
      case 'descricao': return (c.descricao || '').toLowerCase()
      case 'status': return c.status || ''
      default: return ''
    }
  }
  const contasOrdenadas = useMemo(() => {
    if (!sort) return contasFiltradas
    const arr = [...contasFiltradas]
    arr.sort((a, b) => {
      const va = sortVal(a, sort.key), vb = sortVal(b, sort.key)
      if (va < vb) return sort.dir === 'asc' ? -1 : 1
      if (va > vb) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return arr
  }, [contasFiltradas, sort])
  const toggleSort = (key: string) => setSort((s) => s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' })
  const arrow = (key: string) => sort?.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕'

  // Paginação da tabela (só do que é exibido — totais/KPIs seguem sobre a lista filtrada inteira)
  const PAGE_SIZE = 50
  const [pagina, setPagina] = useState(0)
  const totalPaginas = Math.max(1, Math.ceil(contasOrdenadas.length / PAGE_SIZE))
  const paginaSegura = Math.min(pagina, totalPaginas - 1)
  useEffect(() => { setPagina(0) }, [formaPgFiltro, origemFiltro, cgFiltro, vendedorFiltro, busca, dtIni, dtFim, valorMin, valorMax, status, tipo])
  const contasPagina = useMemo(
    () => contasOrdenadas.slice(paginaSegura * PAGE_SIZE, paginaSegura * PAGE_SIZE + PAGE_SIZE),
    [contasOrdenadas, paginaSegura])

  // Totais sumarizados (sempre que filtros mudam)
  const totaisFiltrados = contasFiltradas.reduce((acc: any, c: any) => {
    acc.qtd += 1
    acc.bruto += Number(c.valor || 0)
    acc.pago += Number(c.valor_pago || 0)
    acc.liquido += Number(c.valor_liquido || c.valor || 0)
    return acc
  }, { qtd: 0, bruto: 0, pago: 0, liquido: 0 })

  const baixar = useMutation({
    mutationFn: async () =>
      (await api.post('/financeiro/' + baixaModal.id + '/baixar', {
        valor_pago: parseFloat(baixaForm.valor),
        forma_pagamento: baixaForm.forma,
        data_pagamento: baixaForm.data || undefined,
        juros_valor: jurosCalc,
        observacao: baixaForm.obs || undefined,
        conta_id: baixaForm.conta_id || undefined,
      })).data,
    onSuccess: (data) => {
      toast.success(data.status === 'pago' ? 'Conta quitada!' : 'Pagamento parcial registrado')
      setUltimaBaixa({
        conta: { ...data.conta, saldo_restante: data.saldo_restante },
        pagamento: data.pagamento,
      })
      setBaixaModal(null)
      setBaixaForm({ valor: '', forma: 'dinheiro', obs: '', conta_id: '', data: '', juros: '', juros_pct: '' })
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
    },
  })

  const estornar = useMutation({
    mutationFn: async (id: number) => (await api.post('/financeiro/' + id + '/estornar')).data,
    onSuccess: () => {
      toast.success('Baixa estornada — conta voltou pra aberta (lançamento e saldo revertidos). Pode corrigir e baixar de novo.')
      setDetalheModal(null)
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
    },
  })

  const excluirTitulo = useMutation({
    mutationFn: async (id: number) => (await api.delete('/financeiro/' + id)).data,
    onSuccess: () => {
      toast.success('Título excluído')
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  const alterarCgLote = useMutation({
    mutationFn: async () => (await api.post('/financeiro/contas/alterar-conta-gerencial', {
      ids: Array.from(selecionados),
      conta_gerencial_id: novaCg ? Number(novaCg) : null,
    })).data,
    onSuccess: (r) => {
      toast.success(`${r.alteradas} título(s) reclassificado(s)`)
      setAlterarCgModal(false)
      setNovaCg('')
      setSelecionados(new Set())
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
    },
  })

  const transferirRcaLote = useMutation({
    mutationFn: async () => (await api.post('/financeiro/transferir-rca', {
      conta_ids: Array.from(selecionados),
      codigo_vendedor: novoRca.trim(),
    })).data,
    onSuccess: (r) => {
      toast.success(`${r.atualizados} título(s) transferido(s)`)
      setTransferirRcaModal(false)
      setNovoRca('')
      setSelecionados(new Set())
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao transferir'),
  })

  const salvarEdicao = useMutation({
    mutationFn: async () => (await api.put('/financeiro/' + editarModal.id, {
      descricao: editarModal.descricao,
      pessoa_id: editarModal.pessoa_id ? Number(editarModal.pessoa_id) : null,
      conta_gerencial_id: editarModal.conta_gerencial_id ? Number(editarModal.conta_gerencial_id) : null,
      valor: parseFloat(String(editarModal.valor)),
      vencimento: editarModal.vencimento,
      data_competencia: editarModal.data_competencia || null,
      forma_pagamento: editarModal.forma_pagamento || null,
      juros_pct: parseFloat(String(editarModal.juros_pct || 0)),
      observacao: editarModal.observacao || null,
    })).data,
    onSuccess: () => {
      toast.success('Título atualizado')
      setEditarModal(null)
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const criar = useMutation({
    mutationFn: async () => (await api.post('/financeiro', {
      tipo: novoForm.tipo,
      descricao: novoForm.descricao,
      pessoa_id: novoForm.pessoa_id ? Number(novoForm.pessoa_id) : null,
      conta_gerencial_id: novoForm.conta_gerencial_id ? Number(novoForm.conta_gerencial_id) : null,
      filial_id: novoForm.filial_id ? Number(novoForm.filial_id) : null,
      valor: parseFloat(novoForm.valor),
      vencimento: novoForm.vencimento,
      data_competencia: novoForm.data_competencia || null,
      total_parcelas: Number(novoForm.total_parcelas) || 1,
      parcelas: (Number(novoForm.total_parcelas) > 1 && parcelasCustom.length > 1)
        ? parcelasCustom.map((p) => ({ vencimento: p.vencimento, valor: parseFloat(p.valor) || 0 }))
        : null,
      forma_pagamento: novoForm.forma_pagamento || null,
      juros_pct: parseFloat(novoForm.juros_pct || '0'),
      multa_valor: parseFloat(novoForm.multa_valor || '0'),
      juros_valor: parseFloat(novoForm.juros_valor || '0'),
      cheque_banco: novoForm.forma_pagamento === 'cheque' ? (novoForm.cheque_banco || null) : null,
      cheque_agencia: novoForm.forma_pagamento === 'cheque' ? (novoForm.cheque_agencia || null) : null,
      cheque_numero: novoForm.forma_pagamento === 'cheque' ? (novoForm.cheque_numero || null) : null,
      cheque_bom_para: novoForm.forma_pagamento === 'cheque' ? (novoForm.cheque_bom_para || null) : null,
      observacao: novoForm.observacao || null,
    })).data,
    onSuccess: () => {
      toast.success('Lancado')
      setNovoModal(false)
      setNovoForm(EMPTY_NOVO)
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
    },
  })

  function abrirBaixa(c: any) {
    // Se a conta tem valor_liquido (vinda de marketplace), pré-popula com o líquido
    // (que é o que efetivamente cai na conta bancária). Senão usa valor + juros.
    const liquido = c.valor_liquido != null ? Number(c.valor_liquido) : null
    const comJuros = Number(c.valor_total_com_juros || Number(c.valor) - Number(c.valor_pago))
    const sugerido = liquido != null && liquido > 0 ? liquido : comJuros
    // Forma sugerida: usa a forma_pagamento do título (que vem do MP/Tray) se preenchida
    let formaSugerida = c.forma_pagamento && !['dinheiro','outro',''].includes((c.forma_pagamento || '').toLowerCase())
      ? c.forma_pagamento : 'dinheiro'
    // Título marcado "PIX pendente" (recebido na carga mas pix não confirmou): baixa COMO pix.
    if (formaSugerida === 'pix_pendente') formaSugerida = 'pix'
    const hoje = new Date().toISOString().slice(0, 10)
    setBaixaModal(c)
    setBaixaForm({ valor: String(sugerido.toFixed(2)), forma: formaSugerida, obs: '', conta_id: c.conta_bancaria_id || '', data: hoje, juros: '', juros_pct: '' })
  }

  const labelTipo = tipo === 'receber' ? 'Conta a Receber' : 'Conta a Pagar'

  return (
    <div className="space-y-4">
      {/* Filtros principais */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input flex-1 min-w-[220px]"
            placeholder="Buscar por código, cliente, descrição, NF, pedido, vendedor..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={enterAbrePrimeiro(contasPagina, (c: any) => setDetalheModal(c))}
          />
          <select className="input w-[150px]" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos status</option>
            <option value="em_aberto">{tipo === 'receber' ? 'Em aberto (a receber)' : 'Em aberto (a pagar)'}</option>
            <option value="aberto">Só abertos</option>
            <option value="parcial">Parcial</option>
            <option value="pago">{tipo === 'receber' ? 'Recebido' : 'Pago'}</option>
            <option value="cancelado">Cancelado</option>
          </select>
          <select className="input w-[120px]" value={origemFiltro} onChange={(e) => setOrigemFiltro(e.target.value)}>
            <option value="">Todas origens</option>
            <option value="venda">Venda</option>
            <option value="tiny">Tiny</option>
            <option value="manual">Manual</option>
            <option value="compra">Compra</option>
          </select>
          <select className="input w-[140px]" value={formaPgFiltro} onChange={(e) => setFormaPgFiltro(e.target.value)}>
            <option value="">Todas formas</option>
            <option value="pix">PIX</option>
            <option value="pix_pendente">PIX pendente</option>
            <option value="cheque">Cheque</option>
            <option value="cheque_devolvido">Cheque Devolvido</option>
            <option value="cartao_credito">Cartão crédito</option>
            <option value="cartao_debito">Cartão débito</option>
            <option value="boleto">Boleto</option>
            <option value="dinheiro">Dinheiro</option>
            <option value="prazo">A prazo</option>
            <option value="transferencia">Transf.</option>
          </select>
          <select className="input w-[160px]" value={cgFiltro} onChange={(e) => setCgFiltro(e.target.value)}>
            <option value="">Todas classificações</option>
            {cgs.map((c: any) => (
              <option key={c.id} value={c.id}>{c.codigo} — {c.nome}</option>
            ))}
          </select>
          {tipo === 'receber' && (
            <select className="input w-[180px]" value={vendedorFiltro} onChange={(e) => setVendedorFiltro(e.target.value)}>
              <option value="">Todos vendedores</option>
              {vendedoresDisponiveis.map((v: any) => (
                <option key={v.id} value={v.id}>
                  {v.codigo ? `#${v.codigo} ` : ''}{v.nome}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Chips de período */}
          <div className="flex items-center gap-1 bg-ink-50 rounded-md p-1">
            {([
              ['hoje','Hoje'], ['7d','7d'], ['30d','30d'], ['mes','Este mês'],
              ['mes_ant','Mês ant.'], ['todos','Todos'], ['custom','Custom'],
            ] as const).map(([k, lbl]) => (
              <button key={k}
                onClick={() => setPeriodoPreset(k as any)}
                className={`px-2 py-1 rounded text-xs font-medium ${periodoPreset === k ? 'bg-white shadow-sm text-ink-900' : 'text-ink-600 hover:bg-white/60'}`}
              >{lbl}</button>
            ))}
          </div>
          {periodoPreset === 'custom' && (
            <>
              <input type="date" className="input w-[150px]" value={periodoIni} onChange={(e) => setPeriodoIni(e.target.value)} />
              <span className="text-ink-400">→</span>
              <input type="date" className="input w-[150px]" value={periodoFim} onChange={(e) => setPeriodoFim(e.target.value)} />
            </>
          )}
          <input type="number" placeholder="Valor mín" className="input w-[110px]" value={valorMin} onChange={(e) => setValorMin(e.target.value)} />
          <input type="number" placeholder="Valor máx" className="input w-[110px]" value={valorMax} onChange={(e) => setValorMax(e.target.value)} />
          <button
            onClick={() => {
              setStatus(''); setFormaPgFiltro(''); setOrigemFiltro(''); setCgFiltro('')
              setBusca(''); setValorMin(''); setValorMax(''); setPeriodoPreset('mes')
            }}
            className="btn-secondary text-xs"
          >Limpar</button>
        </div>
        {/* Sumário dos filtros */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-ink-600 bg-ink-50 rounded-md px-3 py-2">
          <span><strong>{totaisFiltrados.qtd}</strong> títulos</span>
          <span>•</span>
          <span>Bruto: <strong className="font-mono">{formatMoney(totaisFiltrados.bruto)}</strong></span>
          <span>•</span>
          <span>{tipo === 'receber' ? 'Recebido' : 'Pago'}: <strong className="font-mono text-emerald-700">{formatMoney(totaisFiltrados.pago)}</strong></span>
          <span>•</span>
          <span>Líquido prev.: <strong className="font-mono text-emerald-800">{formatMoney(totaisFiltrados.liquido)}</strong></span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1" />
        {tipo === 'receber' && (
          <>
            <button
              onClick={async () => {
                const t = toast.loading('Consultando MP...')
                try {
                  const r = await api.post('/financeiro/auto-baixa-mp', { tolerancia: 0.05, max: 200 })
                  toast.dismiss(t)
                  toast.success(`MP: ${r.data.baixadas_auto} baixadas, ${r.data.valor_liquido_atualizado} atualizados, ${r.data.aguardando_liberacao} aguardando, ${r.data.divergentes} divergentes`, { duration: 8000 })
                  qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
                } catch (e: any) {
                  toast.dismiss(t)
                  toast.error(e?.response?.data?.detail || 'Erro')
                }
              }}
              className="btn-secondary"
              title="Consulta MP via API e baixa automaticamente o que já foi liberado"
            >
              <RotateCcw size={14} /> Auto-baixa MP
            </button>
            <button onClick={() => setImportModal(true)} className="btn-secondary">
              <FileSpreadsheet size={14} /> Importar planilha
            </button>
          </>
        )}
        {selecionados.size > 0 && (
          <>
            <span className="text-xs font-semibold text-violet-700 px-2">
              {selecionados.size} selec.
            </span>
            <button
              onClick={() => {
                const sel = contasFiltradas.filter((c: any) => selecionados.has(c.id))
                imprimirRelatorio(sel,
                  (tipo === 'receber' ? 'Contas a Receber' : 'Contas a Pagar') + ' (selecionadas)',
                  `${sel.length} selecionada(s)`)
              }}
              className="btn-secondary"
              title="Imprime apenas as contas selecionadas"
            >
              <Printer size={14} /> Imprimir selec.
            </button>
            <button
              onClick={() => { setNovaCg(''); setAlterarCgModal(true) }}
              className="btn-secondary"
              title="Troca a conta gerencial de todos os títulos selecionados de uma vez"
            >
              <FileSpreadsheet size={14} /> Alterar conta
            </button>
            {tipo === 'receber' && (
              <button
                onClick={() => { setNovoRca(''); setTransferirRcaModal(true) }}
                className="btn-secondary"
                title="Atribui um vendedor (RCA) aos títulos selecionados"
              >
                <Users size={14} /> Transferir por RCA
              </button>
            )}
            {tipo === 'pagar' && (
              <>
                <a href="/financeiro/linhas-digitaveis"
                  className="btn-secondary text-xs flex items-center gap-1.5"
                  title="Tela pra colar a linha digitável de cada conta">
                  <FileSpreadsheet size={12} /> Cadastrar linhas digitáveis
                </a>
                <button
                  onClick={() => setBorderoModal(true)}
                  className="btn-primary bg-emerald-600 hover:bg-emerald-700"
                  title="Gera borderô com aprovação BPM antes de pagar"
                >
                  <FileSpreadsheet size={14} /> Gerar borderô
                </button>
                <button
                  onClick={async () => {
                    const token = localStorage.getItem('pain_token')
                    const t = toast.loading('Gerando PDF...')
                    try {
                      const r = await fetch('/api/financeiro/contas/imprimir-guias-pdf', {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ids: Array.from(selecionados) }),
                      })
                      toast.dismiss(t)
                      if (!r.ok) { toast.error('Erro ao gerar PDF'); return }
                      const blob = await r.blob()
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `guias-${Array.from(selecionados).length}.pdf`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      setTimeout(() => URL.revokeObjectURL(url), 60000)
                      toast.success('PDF baixado')
                    } catch (e: any) {
                      toast.dismiss(t)
                      toast.error('Falha: ' + (e.message || 'erro'))
                    }
                  }}
                  className="btn-secondary"
                  title="Gera PDF com 1 página por guia, com código de barras pra pagar no banco"
                >
                  <Printer size={14} /> Imprimir guias
                </button>
                <button
                  onClick={() => setBaixaLoteModal(true)}
                  className="btn-secondary"
                  title="Baixa direta sem aprovação"
                >
                  <Check size={14} /> Baixar direto
                </button>
              </>
            )}
            <button onClick={() => setSelecionados(new Set())}
              className="text-xs text-ink-500 hover:underline">limpar</button>
          </>
        )}
        <button
          onClick={() => imprimirRelatorio(
            contasFiltradas,
            tipo === 'receber' ? 'Contas a Receber' : 'Contas a Pagar',
            'Status: ' + (status || 'todos') + ' | Forma: ' + (formaPgFiltro || 'todas'),
          )}
          className="btn-secondary"
        >
          <Printer size={14} /> Imprimir tudo
        </button>
        {tipo === 'receber' && <ImpressoesReceberMenu vendedores={vendedoresDisponiveis} />}
        <button onClick={() => {
          // Pré-seleciona a filial atual (a não ser no consolidado, onde o admin escolhe)
          setNovoForm({ ...EMPTY_NOVO, filial_id: (!modoConsolidado && filialAtualId) ? filialAtualId : '' })
          setNovoModal(true)
        }} className="btn-primary">
          <Plus size={14} /> Nova {labelTipo}
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                  checked={contasFiltradas.length > 0 && contasFiltradas.every((c: any) => selecionados.has(c.id))}
                  onChange={() => {
                    const todos = contasFiltradas.every((c: any) => selecionados.has(c.id))
                    if (todos) setSelecionados(new Set())
                    else setSelecionados(new Set(contasFiltradas.map((c: any) => c.id)))
                  }}
                  title="Selecionar/desmarcar tudo"
                />
              </th>
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('cliente')}>{tipo === 'receber' ? 'Cliente / CPF' : 'Fornecedor / CNPJ'}{arrow('cliente')}</th>
              {mostrarFilial && <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('filial')}>Filial{arrow('filial')}</th>}
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('pedido')}>Pedido{arrow('pedido')}</th>
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('nf')}>NF{arrow('nf')}</th>
              {tipo === 'receber' && marketplaceAtivo && <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('ml')}>Pedido ML{arrow('ml')}</th>}
              {tipo === 'receber' && marketplaceAtivo && <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('mp')}>ID MP{arrow('mp')}</th>}
              {tipo === 'receber' && <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('vendedor')}>Vendedor{arrow('vendedor')}</th>}
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('cg')}>Conta Gerencial{arrow('cg')}</th>
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('vencimento')}>Vencimento{arrow('vencimento')}</th>
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('parcela')}>Parcela{arrow('parcela')}</th>
              <th className="text-right cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('valor')}>Valor{arrow('valor')}</th>
              <th className="text-right cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('recebido')}>{tipo === 'receber' ? 'Recebido' : 'Pago'}{arrow('recebido')}</th>
              <th className="text-right cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('juros')}>Juros{arrow('juros')}</th>
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('forma')}>Forma{arrow('forma')}</th>
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('descricao')}>Descricao{arrow('descricao')}</th>
              <th className="cursor-pointer select-none whitespace-nowrap hover:text-ink-900" onClick={() => toggleSort('status')}>Status{arrow('status')}</th>
              <th className="w-40 sticky right-0 bg-ink-50 z-10 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {(() => { const cs = (tipo === 'receber' && marketplaceAtivo ? 15 : 13) + 1 + (mostrarFilial ? 1 : 0); return (<>
            {isLoading && <tr><td colSpan={cs} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && contasFiltradas.length === 0 && (
              <tr><td colSpan={cs} className="text-center py-8">
                {qUrl && !modoConsolidado && filialAtual ? (
                  <div className="text-ink-500 space-y-2">
                    <p>Nenhum título encontrado em <strong>{filialAtual.nome_fantasia}</strong> para "{qUrl}".</p>
                    <p className="text-xs">
                      Pode estar em outra filial —{' '}
                      <button onClick={() => setModoConsolidado(true)}
                        className="text-brand-600 underline hover:text-brand-700">
                        ver todas as filiais
                      </button>
                    </p>
                  </div>
                ) : (
                  <span className="text-ink-500">Nada por aqui.</span>
                )}
              </td></tr>
            )}</>)})()}
            {contasPagina.map((c: any) => {
              const vencido = c.status !== 'pago' && c.status !== 'cancelado' && new Date(c.vencimento) < new Date()
              const fmtDoc = (d: string | null | undefined) => {
                if (!d) return ''
                const n = d.replace(/\D/g, '')
                if (n.length === 11) return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6,9)}-${n.slice(9)}`
                if (n.length === 14) return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8,12)}-${n.slice(12)}`
                return d
              }
              const podeEditar = c.status !== 'pago' && c.status !== 'cancelado' && Number(c.valor_pago || 0) === 0
              return (
                <tr key={c.id}
                  onClick={() => setDetalheModal(c)}
                  className={`hover:bg-violet-50 cursor-pointer ${selecionados.has(c.id) ? 'bg-violet-50/50' : ''}`}>
                  <td className="w-8" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox"
                      checked={selecionados.has(c.id)}
                      onChange={() => {
                        const s = new Set(selecionados)
                        s.has(c.id) ? s.delete(c.id) : s.add(c.id)
                        setSelecionados(s)
                      }}
                    />
                  </td>
                  <td className="text-ink-600 text-xs">
                    <div>{c.cliente_codigo ? `[${c.cliente_codigo}] ` : ''}{c.cliente_nome}</div>
                    {c.cliente_documento && <div className="text-[10px] font-mono text-ink-500">{fmtDoc(c.cliente_documento)}</div>}
                  </td>
                  {mostrarFilial && (
                    <td className="text-xs whitespace-nowrap">
                      {c.filial_nome
                        ? <span className="inline-block px-1.5 py-0.5 rounded bg-ink-100 text-ink-700">{c.filial_nome}</span>
                        : <span className="text-ink-300" title="Sem filial (lançamento geral/legado)">—</span>}
                    </td>
                  )}
                  <td className="text-xs font-mono">
                    {c.pedido_numero ? <span className="text-brand-700">#{c.pedido_numero}</span> : <span className="text-ink-300">—</span>}
                  </td>
                  <td className="text-xs font-mono">
                    {c.numero_doc_simples
                      ? <span className="text-ink-600" title="Documento interno (venda simples, sem NF-e)">{c.numero_doc_simples}<span className="text-ink-400 ml-0.5">·s</span></span>
                      : c.numero_nf ? c.numero_nf : <span className="text-ink-300">—</span>}
                  </td>
                  {tipo === 'receber' && marketplaceAtivo && (
                    <td className="text-xs font-mono">
                      {c.ml_order_id ? (
                        <a href={`https://www.mercadolivre.com.br/vendas/${c.ml_order_id}/detalhe`} target="_blank" rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-amber-700 hover:underline">{c.ml_order_id}</a>
                      ) : <span className="text-ink-300">—</span>}
                    </td>
                  )}
                  {tipo === 'receber' && marketplaceAtivo && (
                    <td className="text-xs font-mono">
                      {c.mp_payment_id ? (
                        <a href={`https://www.mercadopago.com.br/activities/${c.mp_payment_id}`} target="_blank" rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-sky-700 hover:underline">{c.mp_payment_id}</a>
                      ) : <span className="text-ink-300">—</span>}
                    </td>
                  )}
                  {tipo === 'receber' && (
                    <td className="text-xs font-mono">
                      {c.vendedor_id ? (
                        <span className="text-ink-700" title={c.vendedor_nome || ''}>
                          {c.vendedor_codigo ? <span className="text-brand-700">#{c.vendedor_codigo}</span> : ''}
                          <span className="ml-1 truncate inline-block max-w-[120px] align-bottom">{c.vendedor_nome}</span>
                        </span>
                      ) : <span className="text-ink-300">—</span>}
                    </td>
                  )}
                  <td className="text-xs">
                    {c.conta_gerencial_id ? (
                      <span className="font-mono text-ink-700">
                        {c.conta_gerencial_codigo} — {c.conta_gerencial_nome}
                      </span>
                    ) : (
                      <span className="text-amber-600">sem classificacao</span>
                    )}
                  </td>
                  <td className={vencido ? 'text-red-600 font-medium' : ''}>
                    {formatDate(c.vencimento)}
                    {vencido && <AlertTriangle size={12} className="inline ml-1 text-red-500" />}
                  </td>
                  <td className="text-ink-600">{c.parcela}/{c.total_parcelas}</td>
                  <td className="text-right font-medium">{formatMoney(c.valor)}</td>
                  <td className="text-right text-ink-600">{formatMoney(c.valor_pago)}</td>
                  <td className="text-right">
                    {Number(c.juros_valor) > 0 ? (
                      <span className="text-red-600 text-xs font-medium">+{formatMoney(c.juros_valor)}</span>
                    ) : '-'}
                  </td>
                  <td className="text-ink-600 capitalize text-xs">
                    {c.forma_pagamento ? c.forma_pagamento.replace('_', ' ') : '-'}
                  </td>
                  <td className="font-medium">{c.descricao}</td>
                  <td>
                    <span className={
                      c.status === 'pago' ? 'badge-success' :
                      c.status === 'parcial' ? 'badge-warning' :
                      c.status === 'cancelado' ? 'badge-neutral' :
                      vencido ? 'badge-danger' : 'badge-info'
                    }>
                      {vencido && c.status === 'aberto'
                        ? 'vencido'
                        : c.status === 'pago' && tipo === 'receber'
                          ? 'recebido'
                          : c.status === 'aberto' && tipo === 'receber'
                            ? 'a receber'
                            : c.status === 'aberto' && tipo === 'pagar'
                              ? 'a pagar'
                              : c.status}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}
                    className="sticky right-0 bg-white z-[1] shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">
                    <div className="flex gap-1">
                      <button onClick={() => setDetalheModal(c)} className="p-1.5 rounded hover:bg-ink-100 text-ink-600" title="Detalhes">
                        <Eye size={14} />
                      </button>
                      {c.status !== 'pago' && c.status !== 'cancelado' && (
                        <button className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title={tipo === 'receber' ? 'Receber' : 'Pagar'} onClick={() => abrirBaixa(c)}>
                          <Check size={14} />
                        </button>
                      )}
                      {(c.status === 'pago' || c.status === 'parcial') && (
                        <button
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          title="Estornar"
                          onClick={() => { if (confirm('Estornar a baixa? Reverte lançamento + saldo do banco e volta a conta pra ABERTA (pra corrigir e baixar de novo).')) estornar.mutate(c.id) }}
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                      {c.historico_pagamentos && c.historico_pagamentos.length > 0 && (
                        <button
                          className="p-1.5 rounded hover:bg-ink-100 text-ink-600"
                          title="Imprimir recibo"
                          onClick={() => {
                            const last = c.historico_pagamentos[c.historico_pagamentos.length - 1]
                            imprimirRecibo({ descricao: c.descricao, saldo_restante: 0 }, last)
                          }}
                        >
                          <Printer size={14} />
                        </button>
                      )}
                      {podeEditar && (
                        <>
                          <button
                            onClick={() => setEditarModal({ ...c })}
                            className="p-1.5 rounded hover:bg-sky-50 text-sky-600"
                            title="Editar título"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => pedirExclusao({ descricao: `o título "${c.descricao}"`, onConfirm: () => excluirTitulo.mutate(c.id) })}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                            title="Excluir título"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINAÇÃO (totais e KPIs acima continuam somando a lista filtrada inteira) */}
      {contasOrdenadas.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-ink-600">
          <span>
            Mostrando <b>{paginaSegura * PAGE_SIZE + 1}</b>–<b>{Math.min((paginaSegura + 1) * PAGE_SIZE, contasOrdenadas.length)}</b> de <b>{contasOrdenadas.length.toLocaleString('pt-BR')}</b> títulos
          </span>
          <div className="flex items-center gap-2">
            <button className="btn-ghost text-sm disabled:opacity-40"
              onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={paginaSegura === 0}>
              <ChevronLeft size={16} /> Anterior
            </button>
            <span className="px-2">Página {paginaSegura + 1} de {totalPaginas}</span>
            <button className="btn-ghost text-sm disabled:opacity-40"
              onClick={() => setPagina(p => Math.min(totalPaginas - 1, p + 1))} disabled={paginaSegura >= totalPaginas - 1}>
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {importModal && tipo === 'receber' && <ImportarBaixaModal onClose={() => setImportModal(false)} qc={qc} />}

      {baixaLoteModal && (
        <BaixaLoteModal
          ids={Array.from(selecionados)}
          contas={contasFiltradas.filter((c: any) => selecionados.has(c.id))}
          onClose={() => setBaixaLoteModal(false)}
          onDone={() => {
            setBaixaLoteModal(false)
            setSelecionados(new Set())
            qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
          }}
        />
      )}

      {borderoModal && (
        <BorderoModal
          ids={Array.from(selecionados)}
          contas={contasFiltradas.filter((c: any) => selecionados.has(c.id))}
          onClose={() => setBorderoModal(false)}
          onDone={() => {
            setBorderoModal(false)
            setSelecionados(new Set())
            qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
          }}
        />
      )}

      {alterarCgModal && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b">
              <h3 className="font-semibold text-lg">Alterar conta gerencial</h3>
              <p className="text-sm text-ink-500">
                {selecionados.size} título(s) selecionado(s) serão reclassificados.
              </p>
            </div>
            <div className="px-5 py-4">
              <label className="label">Nova conta gerencial</label>
              <ContaGerencialCascata
                items={cgs}
                value={novaCg ? Number(novaCg) : ''}
                onChange={(id) => setNovaCg(id ? String(id) : '')}
                placeholder="Grupo (macro)…"
              />
            </div>
            <div className="px-5 py-4 border-t flex gap-2">
              <button onClick={() => setAlterarCgModal(false)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => alterarCgLote.mutate()} disabled={alterarCgLote.isPending}
                className="btn-primary flex-1">
                {alterarCgLote.isPending ? 'Aplicando...' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {transferirRcaModal && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-semibold flex items-center gap-2"><Users size={16} /> Transferir por RCA</h3>
              <button onClick={() => setTransferirRcaModal(false)} className="p-1 hover:bg-ink-100 rounded"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="text-sm text-ink-700">
                {selecionados.size} título(s) serão transferidos.
              </div>
              <div>
                <label className="label">Código do vendedor (RCA)</label>
                <input type="number" inputMode="numeric" autoFocus
                  className="input text-lg font-bold font-mono"
                  placeholder="ex: 5"
                  value={novoRca}
                  onChange={(e) => setNovoRca(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && novoRca) transferirRcaLote.mutate() }} />
                <p className="text-xs text-ink-500 mt-1">Deixe vazio (ou 0) pra remover o vendedor.</p>
              </div>
            </div>
            <div className="px-5 py-4 border-t flex gap-2">
              <button onClick={() => setTransferirRcaModal(false)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => transferirRcaLote.mutate()} disabled={transferirRcaLote.isPending}
                className="btn-primary flex-1">
                {transferirRcaLote.isPending ? 'Aplicando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detalheModal && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-200">
              <h3 className="font-semibold text-lg">Detalhes do Titulo</h3>
              <button onClick={() => setDetalheModal(null)} className="p-1 hover:bg-ink-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-ink-500 block text-xs">Descricao</span><span className="font-medium">{detalheModal.descricao}</span></div>
                <div><span className="text-ink-500 block text-xs">Codigo</span><span className="font-mono font-medium">{detalheModal.cliente_codigo || '-'}</span></div><div><span className="text-ink-500 block text-xs">Cliente</span><span className="font-medium">{detalheModal.cliente_nome}</span></div>
                <div><span className="text-ink-500 block text-xs">Valor</span><span className="font-bold text-lg">{formatMoney(detalheModal.valor)}</span></div>
                <div><span className="text-ink-500 block text-xs">{tipo === 'receber' ? 'Valor Recebido' : 'Valor Pago'}</span><span className="font-bold text-lg">{formatMoney(detalheModal.valor_pago)}</span></div>
                <div><span className="text-ink-500 block text-xs">Vencimento</span><span>{formatDate(detalheModal.vencimento)}</span></div>
                <div><span className="text-ink-500 block text-xs">Status</span>
                  <span className={detalheModal.status === 'pago' ? 'badge-success' : detalheModal.status === 'parcial' ? 'badge-warning' : 'badge-info'}>
                    {detalheModal.status === 'pago' && tipo === 'receber' ? 'recebido'
                     : detalheModal.status === 'aberto' && tipo === 'receber' ? 'a receber'
                     : detalheModal.status === 'aberto' && tipo === 'pagar' ? 'a pagar'
                     : detalheModal.status}
                  </span>
                </div>
                <div><span className="text-ink-500 block text-xs">Parcela</span><span>{detalheModal.parcela}/{detalheModal.total_parcelas}</span></div>
                <div><span className="text-ink-500 block text-xs">Forma Pagamento</span><span className="capitalize">{detalheModal.forma_pagamento?.replace('_', ' ') || '-'}</span></div>
                <div><span className="text-ink-500 block text-xs">Origem</span><span className="capitalize">{detalheModal.origem}</span></div>
                <div><span className="text-ink-500 block text-xs">Juros (%/mes)</span><span>{detalheModal.juros_pct}%</span></div>
                {Number(detalheModal.juros_valor) > 0 && (
                  <div><span className="text-ink-500 block text-xs">Juros Calculado</span><span className="text-red-600 font-medium">{formatMoney(detalheModal.juros_valor)}</span></div>
                )}
                {detalheModal.numero_nf && (
                  <div><span className="text-ink-500 block text-xs">Numero NF</span><span className="font-mono">{detalheModal.numero_nf}</span></div>
                )}
                {detalheModal.ml_order_id && (
                  <div><span className="text-ink-500 block text-xs">Pedido ML</span>
                    <a href={`https://www.mercadolivre.com.br/vendas/${detalheModal.ml_order_id}/detalhe`} target="_blank" rel="noreferrer"
                      className="font-mono text-amber-700 hover:underline">{detalheModal.ml_order_id}</a>
                  </div>
                )}
                {detalheModal.mp_payment_id && (
                  <div><span className="text-ink-500 block text-xs">ID Mercado Pago</span>
                    <a href={`https://www.mercadopago.com.br/activities/${detalheModal.mp_payment_id}`} target="_blank" rel="noreferrer"
                      className="font-mono text-sky-700 hover:underline">{detalheModal.mp_payment_id}</a>
                  </div>
                )}
                <div className="col-span-2"><span className="text-ink-500 block text-xs">Conta Gerencial</span>
                  <span>{detalheModal.conta_gerencial_id
                    ? detalheModal.conta_gerencial_codigo + ' — ' + detalheModal.conta_gerencial_nome
                    : <span className="text-amber-600">sem classificacao</span>}
                  </span>
                </div>
                <div className="col-span-2"><span className="text-ink-500 block text-xs">Criado em</span>
                  <span>{detalheModal.criado_em ? new Date(detalheModal.criado_em).toLocaleString('pt-BR') : '-'}</span>
                </div>
              </div>
              {(detalheModal.taxa_marketplace != null || detalheModal.valor_liquido != null) && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                  <div className="text-xs font-semibold text-emerald-900 mb-2 flex items-center justify-between">
                    <span>Composição do recebível</span>
                    {detalheModal.detalhes_taxas?.fonte === 'estimado' && (
                      <span className="text-[10px] font-normal text-amber-700 bg-amber-100 px-1.5 rounded">estimado {(Number(detalheModal.detalhes_taxas?.percentual_comissao || 0) * 100).toFixed(1)}%</span>
                    )}
                    {detalheModal.detalhes_taxas?.fonte === 'ml' && (
                      <span className="text-[10px] font-normal text-emerald-800 bg-emerald-200 px-1.5 rounded">ML real</span>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between font-medium"><span className="text-ink-700">Valor bruto</span><span className="font-mono">{formatMoney(detalheModal.valor)}</span></div>
                    {/* Comissão marketplace — quebra em sale_fee + marketplace_fee se existir detalhe */}
                    {detalheModal.detalhes_taxas?.sale_fee_itens && (
                      <div className="flex justify-between text-red-600 pl-3 text-xs"><span>↳ comissão de venda (ML sale_fee)</span><span className="font-mono">- {formatMoney(detalheModal.detalhes_taxas.sale_fee_itens)}</span></div>
                    )}
                    {detalheModal.detalhes_taxas?.marketplace_fee_payments && Number(detalheModal.detalhes_taxas.marketplace_fee_payments) > 0 && (
                      <div className="flex justify-between text-red-600 pl-3 text-xs"><span>↳ taxa do gateway (MP fee)</span><span className="font-mono">- {formatMoney(detalheModal.detalhes_taxas.marketplace_fee_payments)}</span></div>
                    )}
                    <div className="flex justify-between text-red-700"><span>(−) Comissão marketplace (total)</span><span className="font-mono">- {formatMoney(detalheModal.taxa_marketplace ?? 0)}</span></div>
                    <div className="flex justify-between text-red-700"><span>(−) Frete seller</span><span className="font-mono">- {formatMoney(detalheModal.taxa_frete_seller ?? 0)}</span></div>
                    <div className="flex justify-between border-t border-emerald-300 pt-1 mt-1 font-bold text-emerald-800">
                      <span>= Valor líquido (cai na conta)</span>
                      <span className="font-mono">{formatMoney(detalheModal.valor_liquido ?? (Number(detalheModal.valor) - Number(detalheModal.taxa_marketplace || 0) - Number(detalheModal.taxa_frete_seller || 0)))}</span>
                    </div>
                  </div>
                </div>
              )}
              {detalheModal.observacao && (
                <div>
                  <span className="text-ink-500 block text-xs mb-1">Observacoes / Notas</span>
                  <div className="bg-ink-50 rounded-lg p-3 text-sm">{detalheModal.observacao}</div>
                </div>
              )}
              {detalheModal.historico_pagamentos && detalheModal.historico_pagamentos.length > 0 && (
                <div>
                  <span className="text-ink-500 block text-xs mb-2">Historico de Pagamentos</span>
                  <div className="space-y-2">
                    {detalheModal.historico_pagamentos.map((p: any, i: number) => (
                      <div key={i} className="bg-ink-50 rounded-lg p-3 flex justify-between items-center text-sm">
                        <div>
                          <div className="font-medium">{formatMoney(p.valor)} — <span className="capitalize">{(p.forma || '').replace('_', ' ')}</span></div>
                          <div className="text-xs text-ink-500">{p.data}{p.observacao ? ' — ' + p.observacao : ''}</div>
                        </div>
                        <button
                          onClick={() => imprimirRecibo({ descricao: detalheModal.descricao, saldo_restante: 0 }, p)}
                          className="p-1.5 rounded hover:bg-ink-200 text-ink-600"
                        >
                          <Printer size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-ink-200 flex gap-2">
              {detalheModal.status !== 'pago' && detalheModal.status !== 'cancelado' && (
                <button
                  onClick={() => { setDetalheModal(null); abrirBaixa(detalheModal) }}
                  className="btn-primary flex-1"
                >
                  <Check size={14} /> {tipo === 'receber' ? 'Receber' : 'Pagar'}
                </button>
              )}
              {(detalheModal.status === 'pago' || detalheModal.status === 'parcial') && (
                <button
                  onClick={() => { if (confirm('Estornar a baixa? Reverte lançamento + saldo do banco e volta a conta pra ABERTA (pra corrigir e baixar de novo).')) estornar.mutate(detalheModal.id) }}
                  className="btn-danger flex-1"
                >
                  <RotateCcw size={14} /> Estornar
                </button>
              )}
              <button onClick={() => setDetalheModal(null)} className="btn-secondary flex-1">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {ultimaBaixa && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Check size={32} className="text-emerald-600" />
            </div>
            <h3 className="font-semibold text-lg mb-1">Pagamento Registrado</h3>
            <p className="text-sm text-ink-500 mb-3">{ultimaBaixa.conta.descricao}</p>
            <div className="bg-ink-50 rounded-lg p-3 mb-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Valor pago:</span><span className="font-bold">{formatMoney(ultimaBaixa.pagamento.valor)}</span></div>
              <div className="flex justify-between"><span>Forma:</span><span className="capitalize">{ultimaBaixa.pagamento.forma.replace('_', ' ')}</span></div>
              <div className="flex justify-between"><span>Saldo restante:</span><span className="font-bold">{formatMoney(ultimaBaixa.conta.saldo_restante)}</span></div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => imprimirRecibo(ultimaBaixa.conta, ultimaBaixa.pagamento)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-800 font-medium"
              >
                <Printer size={16} /> Recibo
              </button>
              <button onClick={() => setUltimaBaixa(null)} className="flex-1 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-medium">OK</button>
            </div>
          </div>
        </div>
      )}

      {baixaModal && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-200 shrink-0">
              <div>
                <h3 className="font-semibold text-lg">Registrar Pagamento</h3>
                <p className="text-xs text-ink-500 mt-0.5">{baixaModal.descricao} — {baixaModal.cliente_codigo ? `[${baixaModal.cliente_codigo}] ` : ''}{baixaModal.cliente_nome}</p>
              </div>
              <button onClick={() => setBaixaModal(null)} className="p-1 hover:bg-ink-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-auto flex-1 min-h-0">
              <div className="bg-ink-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-ink-600">Valor original:</span><span className="font-medium">{formatMoney(baixaModal.valor)}</span></div>
                <div className="flex justify-between"><span className="text-ink-600">Ja pago:</span><span>{formatMoney(baixaModal.valor_pago)}</span></div>
                <div className="flex justify-between"><span className="text-ink-600">Saldo devedor:</span><span className="font-bold">{formatMoney(Number(baixaModal.valor) - Number(baixaModal.valor_pago))}</span></div>
                {Number(baixaModal.juros_valor) > 0 && (
                  <>
                    <div className="flex justify-between text-red-600"><span>Juros ({baixaModal.juros_pct}%/mes):</span><span className="font-bold">+{formatMoney(baixaModal.juros_valor)}</span></div>
                    <div className="flex justify-between border-t border-ink-200 pt-1 mt-1"><span className="font-bold">Total com juros:</span><span className="font-bold text-lg">{formatMoney(baixaModal.valor_total_com_juros)}</span></div>
                  </>
                )}
              </div>
              <div>
                <label className="label">Valor a pagar</label>
                <input type="number" step="0.01" className="input text-lg font-bold" value={baixaForm.valor} onChange={(e) => setBaixaForm((f) => ({ ...f, valor: e.target.value }))} />
                {(() => {
                  const v = parseFloat(baixaForm.valor || '0')
                  const saldoTotal = Number(baixaModal.valor_total_com_juros ?? (Number(baixaModal.valor) - Number(baixaModal.valor_pago)))
                  if (!v || v <= 0) return <p className="text-xs text-ink-500 mt-1">Pode pagar parcialmente — o que sobrar fica em aberto.</p>
                  if (v < saldoTotal - 0.005) return (
                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 text-xs">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                      <span><strong>Baixa PARCIAL.</strong> Sobram <strong>{formatMoney(saldoTotal - v)}</strong> em aberto — a conta fica como <strong>“parcial”</strong> (não quita).</span>
                    </div>
                  )
                  if (v > saldoTotal + 0.005) return (
                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-rose-700 text-xs">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                      <span>Valor <strong>maior</strong> que o saldo ({formatMoney(saldoTotal)}). Confere o valor.</span>
                    </div>
                  )
                  return <p className="text-xs text-emerald-700 mt-1 font-medium">Quita o título integralmente.</p>
                })()}
              </div>
              {tipo === 'pagar' && (
                <div>
                  <label className="label">Juros / multa</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input type="number" step="0.01" min="0" className="input" placeholder="0,00 %"
                        value={baixaForm.juros_pct} onChange={(e) => setBaixaForm((f) => ({ ...f, juros_pct: e.target.value }))} />
                      <p className="text-[11px] text-ink-400 mt-0.5">% sobre o valor</p>
                    </div>
                    <div>
                      <input type="number" step="0.01" min="0" className="input" placeholder="0,00 R$"
                        value={baixaForm.juros} onChange={(e) => setBaixaForm((f) => ({ ...f, juros: e.target.value }))} />
                      <p className="text-[11px] text-ink-400 mt-0.5">valor fixo (R$)</p>
                    </div>
                  </div>
                  <p className="text-xs text-ink-500 mt-1">
                    Soma ao pagamento — sai do banco junto.
                    {jurosCalc > 0 && (
                      <span className="font-medium text-ink-700"> Juros: {formatMoney(jurosCalc)} · Total: {formatMoney((parseFloat(baixaForm.valor || '0') || 0) + jurosCalc)}</span>
                    )}
                  </p>
                </div>
              )}
              <div>
                <label className="label">Forma de pagamento</label>
                <div className="grid grid-cols-3 gap-2">
                  {FORMAS.map((f) => {
                    const Icon = f.icon
                    return (
                      <button
                        key={f.id}
                        onClick={() => setBaixaForm((fm) => ({ ...fm, forma: f.id }))}
                        className={`py-2.5 rounded-lg text-xs font-medium flex flex-col items-center gap-1 transition-all border-2 ${
                          baixaForm.forma === f.id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-ink-200 hover:border-ink-300 text-ink-700'
                        }`}
                      >
                        <Icon size={18} />{f.label}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <label className="label">
                  {tipo === 'pagar' ? 'Banco / conta de saída' : 'Banco / conta de entrada'}
                  {['pix', 'dinheiro', 'transferencia', 'ted', 'doc'].includes(baixaForm.forma) && (
                    <span className="text-rose-600 ml-1">*</span>
                  )}
                </label>
                <select
                  className={`input ${
                    ['pix', 'dinheiro', 'transferencia', 'ted', 'doc'].includes(baixaForm.forma) && !baixaForm.conta_id
                      ? 'border-amber-400 bg-amber-50/40'
                      : ''
                  }`}
                  value={baixaForm.conta_id}
                  onChange={(e) => setBaixaForm((f) => ({ ...f, conta_id: e.target.value ? Number(e.target.value) : '' }))}>
                  <option value="">Selecione o banco...</option>
                  {bancos.map((b: any) => (
                    <option key={b.id} value={b.id}>{b.nome}</option>
                  ))}
                </select>
                {baixaForm.forma === 'pix' && !baixaForm.conta_id && (
                  <p className="text-xs text-amber-700 mt-1">Qual banco recebeu o pix? Sem isso o saldo não atualiza certo.</p>
                )}
              </div>
              <div>
                <label className="label">
                  {baixaForm.forma === 'pix' ? 'Data do PIX' : 'Data do pagamento'}
                  {baixaForm.forma === 'pix' && <span className="text-rose-600 ml-1">*</span>}
                </label>
                <input type="date"
                  className={`input ${baixaForm.forma === 'pix' && !baixaForm.data ? 'border-amber-400 bg-amber-50/40' : ''}`}
                  value={baixaForm.data}
                  onChange={(e) => setBaixaForm((f) => ({ ...f, data: e.target.value }))} />
                <p className="text-xs text-ink-500 mt-1">
                  {baixaForm.forma === 'pix'
                    ? 'Dia em que o PIX caiu — é essa data que sai no movimento financeiro.'
                    : 'Data que entra no movimento financeiro (padrão: hoje).'}
                </p>
              </div>
              <div>
                <label className="label">Observacao</label>
                <input className="input" placeholder="Ex: pagamento parcial..." value={baixaForm.obs} onChange={(e) => setBaixaForm((f) => ({ ...f, obs: e.target.value }))} />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-ink-200 flex gap-2 shrink-0">
              <button onClick={() => setBaixaModal(null)} className="btn-secondary flex-1">Cancelar</button>
              <button
                onClick={() => baixar.mutate()}
                disabled={
                  baixar.isPending
                  || !baixaForm.valor
                  || parseFloat(baixaForm.valor) <= 0
                  || (tipo === 'pagar' && !baixaForm.conta_id)
                  || (['pix', 'dinheiro', 'transferencia', 'ted', 'doc'].includes(baixaForm.forma) && !baixaForm.conta_id)
                  || (baixaForm.forma === 'pix' && !baixaForm.data)
                }
                className="btn-primary flex-1"
              >
                {baixar.isPending
                  ? <><Loader2 size={14} className="animate-spin" /> Processando</>
                  : (() => {
                      const v = parseFloat(baixaForm.valor || '0')
                      const saldoTotal = Number(baixaModal.valor_total_com_juros ?? (Number(baixaModal.valor) - Number(baixaModal.valor_pago)))
                      const parcial = v > 0 && v < saldoTotal - 0.005
                      return <><Check size={14} /> {parcial ? 'Confirmar baixa parcial' : 'Confirmar'}</>
                    })()}
              </button>
            </div>
          </div>
        </div>
      )}

      {novoModal && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
              <h3 className="font-semibold text-lg">Nova {labelTipo}</h3>
              <button onClick={() => setNovoModal(false)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 overflow-auto flex-1 min-h-0">
              <div>
                <label className="label">Descricao</label>
                <input className="input" value={novoForm.descricao} onChange={(e) => setNovoForm((f) => ({ ...f, descricao: e.target.value }))} />
              </div>
              <div>
                <label className="label">{tipo === 'receber' ? 'Cliente' : 'Fornecedor'}</label>
                <PessoaCombo
                  value={novoForm.pessoa_id}
                  onChange={(id) => setNovoForm((f) => ({ ...f, pessoa_id: id }))}
                  placeholder={tipo === 'receber' ? 'Digite o nome do cliente...' : 'Digite o nome do fornecedor...'}
                  pessoas={pessoas.filter((p: any) =>
                    tipo === 'receber'
                      ? p.tipo === 'cliente' || p.tipo === 'ambos'
                      : p.tipo === 'fornecedor' || p.tipo === 'ambos',
                  )}
                />
              </div>
              <div>
                <label className="label">Conta Gerencial</label>
                <ContaGerencialCascata
                  items={cgs}
                  value={novoForm.conta_gerencial_id}
                  onChange={(id) => setNovoForm((f) => ({ ...f, conta_gerencial_id: id }))}
                  placeholder="Grupo (macro)…"
                />
                <p className="text-xs text-ink-500 mt-1">Classifica este titulo na DRE.</p>
              </div>
              {filiais.length > 0 && (
                <div>
                  <label className="label">Filial</label>
                  <select className="input" value={novoForm.filial_id}
                    onChange={(e) => setNovoForm((f) => ({ ...f, filial_id: e.target.value ? Number(e.target.value) : '' }))}>
                    <option value="">— sem filial (geral) —</option>
                    {filiais.map((fl: any) => (
                      <option key={fl.id} value={fl.id}>
                        {fl.nome_fantasia || fl.razao_social} {fl.codigo ? `(${fl.codigo})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-ink-500 mt-1">
                    Loja a que esta {tipo === 'receber' ? 'entrada' : 'saída'} pertence. Já vem na filial atual; o admin pode trocar.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Valor</label>
                  <input type="number" step="0.01" className="input" value={novoForm.valor} onChange={(e) => setNovoForm((f) => ({ ...f, valor: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Vencimento</label>
                  <input type="date" className="input" value={novoForm.vencimento} onChange={(e) => setNovoForm((f) => ({ ...f, vencimento: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Competência <span className="text-ink-400 font-normal">(opcional)</span></label>
                  <input type="date" className="input" value={novoForm.data_competencia}
                    onChange={(e) => setNovoForm((f) => ({ ...f, data_competencia: e.target.value }))} title="Mês a que a receita/despesa pertence. Vazio = usa o vencimento." />
                </div>
                <div>
                  <label className="label">Parcelas</label>
                  <input type="number" min={1} className="input" value={novoForm.total_parcelas}
                    onChange={(e) => setNovoForm((f) => ({ ...f, total_parcelas: Number(e.target.value) || 1 }))} />
                </div>
              </div>
              {/* Parcelamento inteligente: atalhos + preview do que será gerado */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-ink-500">Gerar parcelas:</span>
                {[1, 2, 3, 6, 12].map((n) => (
                  <button key={n} type="button"
                    onClick={() => setNovoForm((f) => ({ ...f, total_parcelas: n }))}
                    className={`text-xs px-2.5 py-1 rounded border ${Number(novoForm.total_parcelas) === n ? 'bg-brand-600 text-white border-brand-600' : 'bg-white border-ink-200 text-ink-600 hover:border-ink-300'}`}>
                    {n === 1 ? 'à vista' : `${n}x`}
                  </button>
                ))}
              </div>
              {parcelasCustom.length >= 2 && (
                <div className="bg-ink-50 border border-ink-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-ink-600">Vencimentos das parcelas <span className="font-normal text-ink-400">— edite data/valor; ↓ aplica o dia nas seguintes</span></span>
                    <span className="text-xs font-mono font-semibold">Total: {formatMoney(parcelasCustom.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0))}</span>
                  </div>
                  <div className="max-h-48 overflow-auto space-y-1">
                    {parcelasCustom.map((p, idx) => (
                      <div key={idx} className="flex items-center gap-1.5">
                        <span className="text-xs text-ink-500 w-9">{idx + 1}/{parcelasCustom.length}</span>
                        <input type="date" className="input !py-1 text-xs flex-1" value={p.vencimento}
                          onChange={(e) => setParcelasCustom((arr) => arr.map((x, i) => i === idx ? { ...x, vencimento: e.target.value } : x))} />
                        <input type="number" step="0.01" min="0" className="input !py-1 text-xs w-24 text-right font-mono" value={p.valor}
                          onChange={(e) => setParcelasCustom((arr) => arr.map((x, i) => i === idx ? { ...x, valor: e.target.value } : x))} />
                        <button type="button" title="Aplicar este dia do mês nas parcelas seguintes"
                          disabled={idx === parcelasCustom.length - 1} onClick={() => aplicarDiaAbaixo(idx)}
                          className="text-ink-400 hover:text-brand-600 disabled:opacity-30 px-1 font-bold">↓</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Forma (opcional)</label>
                  <select className="input" value={novoForm.forma_pagamento} onChange={(e) => setNovoForm((f) => ({ ...f, forma_pagamento: e.target.value }))}>
                    <option value="">-- indefinido --</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="pix">PIX</option>
                    <option value="boleto">Boleto</option>
                    <option value="cartao_credito">Cartao Credito</option>
                    <option value="cartao_debito">Cartao Debito</option>
                    <option value="prazo">A Prazo</option>
                    {tipo === 'receber' && <option value="cheque">CH - Cheque</option>}
                  </select>
                </div>
                <div>
                  <label className="label">Juros (%/mes)</label>
                  <input type="number" step="0.01" className="input" value={novoForm.juros_pct}
                    onChange={(e) => setNovoForm((f) => ({ ...f, juros_pct: e.target.value }))} />
                </div>
              </div>
              {/* Multa e Juros em VALOR (R$) — só contas a PAGAR; somam ao valor do título */}
              {tipo === 'pagar' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Multa (R$)</label>
                      <input type="number" step="0.01" min="0" className="input" placeholder="0,00"
                        value={novoForm.multa_valor}
                        onChange={(e) => setNovoForm((f) => ({ ...f, multa_valor: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Juros (R$)</label>
                      <input type="number" step="0.01" min="0" className="input" placeholder="0,00"
                        value={novoForm.juros_valor}
                        onChange={(e) => setNovoForm((f) => ({ ...f, juros_valor: e.target.value }))} />
                    </div>
                  </div>
                  {(Number(novoForm.multa_valor) > 0 || Number(novoForm.juros_valor) > 0) && novoForm.valor && (
                    <p className="text-xs text-ink-500 -mt-2">
                      Total a pagar:{' '}
                      <strong>{formatMoney((parseFloat(novoForm.valor) || 0) + (parseFloat(novoForm.multa_valor) || 0) + (parseFloat(novoForm.juros_valor) || 0))}</strong>
                      {' '}(valor + multa + juros)
                    </p>
                  )}
                </>
              )}
              {/* Dados do cheque (obrigatórios quando forma = CH - Cheque, só receber) */}
              {tipo === 'receber' && novoForm.forma_pagamento === 'cheque' && (
                <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-3 space-y-2">
                  <div className="text-xs font-semibold text-amber-800">Dados do cheque (obrigatório)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className="input" placeholder="Banco *" value={novoForm.cheque_banco}
                      onChange={(e) => setNovoForm((f) => ({ ...f, cheque_banco: e.target.value }))} />
                    <input className="input" placeholder="Agência" value={novoForm.cheque_agencia}
                      onChange={(e) => setNovoForm((f) => ({ ...f, cheque_agencia: e.target.value }))} />
                    <input className="input" placeholder="Nº do cheque *" value={novoForm.cheque_numero}
                      onChange={(e) => setNovoForm((f) => ({ ...f, cheque_numero: e.target.value }))} />
                    <div>
                      <span className="text-[11px] text-ink-500">Bom para *</span>
                      <input type="date" className="input" value={novoForm.cheque_bom_para}
                        onChange={(e) => setNovoForm((f) => ({ ...f, cheque_bom_para: e.target.value }))} />
                    </div>
                  </div>
                  <p className="text-[11px] text-amber-700">Entra na Carteira de Cheques (status "carteira") vinculado a este título.</p>
                </div>
              )}
              <div>
                <label className="label">Observacao</label>
                <textarea className="input" rows={2} value={novoForm.observacao} onChange={(e) => setNovoForm((f) => ({ ...f, observacao: e.target.value }))} />
              </div>
            </div>
            <div className="px-5 py-4 border-t flex gap-2 shrink-0">
              <button onClick={() => setNovoModal(false)} className="btn-secondary flex-1">Cancelar</button>
              <button
                onClick={() => criar.mutate()}
                disabled={!novoForm.descricao || !novoForm.valor || criar.isPending
                  || (novoForm.forma_pagamento === 'cheque' && !(novoForm.cheque_banco && novoForm.cheque_numero && novoForm.cheque_bom_para))}
                className="btn-primary flex-1"
              >
                {criar.isPending ? <><Loader2 size={14} className="animate-spin" /> Salvando</> : 'Lancar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar título */}
      {editarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-semibold">
                Editar {tipo === 'receber' ? 'Conta a Receber' : 'Conta a Pagar'} #{editarModal.id}
              </h3>
              <button onClick={() => setEditarModal(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="label">Descrição</label>
                <input className="input" value={editarModal.descricao || ''}
                  onChange={e => setEditarModal({ ...editarModal, descricao: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{tipo === 'receber' ? 'Cliente' : 'Fornecedor'}</label>
                  <PessoaCombo
                    value={editarModal.pessoa_id || ''}
                    onChange={(id) => setEditarModal({ ...editarModal, pessoa_id: id || null })}
                    placeholder={tipo === 'receber' ? 'Digite o nome do cliente...' : 'Digite o nome do fornecedor...'}
                    pessoas={pessoas.filter((p: any) => tipo === 'receber'
                      ? (p.tipo === 'cliente' || p.tipo === 'ambos')
                      : (p.tipo === 'fornecedor' || p.tipo === 'ambos'))}
                  />
                </div>
                <div>
                  <label className="label">Conta Gerencial</label>
                  <ContaGerencialCascata
                    items={cgs}
                    value={editarModal.conta_gerencial_id || ''}
                    onChange={(id) => setEditarModal({ ...editarModal, conta_gerencial_id: id || null })}
                    placeholder="Grupo (macro)…"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Valor</label>
                  <input className="input" type="number" step="0.01" min="0"
                    value={editarModal.valor || ''}
                    onChange={e => setEditarModal({ ...editarModal, valor: e.target.value })} />
                </div>
                <div>
                  <label className="label">Vencimento</label>
                  <input className="input" type="date" value={editarModal.vencimento || ''}
                    onChange={e => setEditarModal({ ...editarModal, vencimento: e.target.value })} />
                </div>
                <div>
                  <label className="label">Competência</label>
                  <input className="input" type="date" value={(editarModal.data_competencia || '').slice(0, 10)}
                    onChange={e => setEditarModal({ ...editarModal, data_competencia: e.target.value })} />
                </div>
                <div>
                  <label className="label">Forma pagamento</label>
                  <select className="input" value={editarModal.forma_pagamento || ''}
                    onChange={e => setEditarModal({ ...editarModal, forma_pagamento: e.target.value })}>
                    <option value="">—</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="pix">PIX</option>
                    <option value="cartao_credito">Cartão crédito</option>
                    <option value="cartao_debito">Cartão débito</option>
                    <option value="boleto">Boleto</option>
                    <option value="prazo">Faturado</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Juros (% a.m.)</label>
                  <input className="input" type="number" step="0.01" min="0"
                    value={editarModal.juros_pct || 0}
                    onChange={e => setEditarModal({ ...editarModal, juros_pct: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="label">Observação</label>
                <textarea className="input" rows={2} value={editarModal.observacao || ''}
                  onChange={e => setEditarModal({ ...editarModal, observacao: e.target.value })} />
              </div>
            </div>
            <div className="px-5 py-3 bg-ink-50 border-t flex justify-end gap-2">
              <button onClick={() => setEditarModal(null)} className="btn-secondary">Cancelar</button>
              <button onClick={() => salvarEdicao.mutate()} className="btn-primary"
                disabled={salvarEdicao.isPending || !editarModal.descricao || !editarModal.valor}>
                {salvarEdicao.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


function ImportarBaixaModal({ onClose, qc }: { onClose: () => void; qc: any }) {
  const [linhas, setLinhas] = useState<any[]>([])
  const [resultado, setResultado] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  function parseCSV(texto: string): any[] {
    const linhasArq = texto.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
    if (linhasArq.length === 0) return []
    const sep = linhasArq[0].includes(';') ? ';' : ','
    const headers = linhasArq[0].split(sep).map(h => h.trim().toLowerCase())
    const idxId = headers.findIndex(h => /^(id|identificador|mp|mp_id|payment|oc|pedido|nf|numero_nf|tiny_id)/i.test(h))
    const idxValor = headers.findIndex(h => /(valor|recebido|liquido|total)/i.test(h))
    const idxData = headers.findIndex(h => /(data|pago|recebido_em)/i.test(h))
    const idxForma = headers.findIndex(h => /(forma|metodo|tipo)/i.test(h))
    if (idxId < 0 || idxValor < 0) return []
    const out: any[] = []
    for (let i = 1; i < linhasArq.length; i++) {
      const cols = linhasArq[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''))
      const ident = cols[idxId]
      const valor = cols[idxValor]?.replace(/[^\d,.-]/g, '').replace('.', '').replace(',', '.')
      if (!ident || !valor) continue
      out.push({
        identificador: ident,
        valor_recebido: parseFloat(valor),
        data: idxData >= 0 ? cols[idxData] : undefined,
        forma: idxForma >= 0 ? cols[idxForma] : undefined,
      })
    }
    return out
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    const texto = await f.text()
    const parsed = parseCSV(texto)
    if (parsed.length === 0) {
      toast.error('Nenhuma linha lida. CSV precisa colunas: identificador (id/MP/OC/NF/tiny_id) + valor.')
      return
    }
    setLinhas(parsed)
    setResultado(null)
  }

  async function aplicar() {
    if (linhas.length === 0) return
    setLoading(true)
    try {
      const r = await api.post('/financeiro/baixar-em-lote', {
        linhas, dry_run: false, tolerancia: 0.01,
      })
      setResultado(r.data)
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
      toast.success(`${r.data.aplicadas} baixas aplicadas, ${r.data.erros} divergências`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <FileSpreadsheet size={16} /> Importar baixas por planilha
          </h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
            <div className="font-semibold mb-1">Como funciona:</div>
            <ul className="list-disc ml-5 space-y-0.5">
              <li>CSV com colunas: <code>identificador</code> (ID VELORE ERP, MP id, OC ML, NF ou tiny_id) e <code>valor</code> (recebido)</li>
              <li>Opcional: <code>data</code> (YYYY-MM-DD) e <code>forma</code></li>
              <li>Compara o valor da planilha com o <strong>valor líquido esperado</strong> (tolerância R$ 0,01)</li>
              <li><strong>Divergente não baixa</strong> — relatado na tela. Sem divergência baixa direto.</li>
            </ul>
          </div>

          {linhas.length === 0 ? (
            <label className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-ink-300 rounded-lg cursor-pointer hover:bg-ink-50">
              <Upload size={32} className="text-ink-400 mb-2" />
              <span className="text-sm text-ink-700">Clique para selecionar CSV ou XLSX (CSV preferível)</span>
              <span className="text-xs text-ink-500 mt-1">Use vírgula ou ponto-e-vírgula como separador</span>
              <input type="file" accept=".csv,.txt,text/csv" className="hidden" onChange={onFile} />
            </label>
          ) : (
            <>
              <div className="text-sm">
                <strong>{linhas.length}</strong> linhas detectadas. {!resultado && (
                  <button onClick={() => { setLinhas([]); setResultado(null) }}
                    className="text-brand-700 underline ml-2 text-xs">Trocar arquivo</button>
                )}
              </div>
              {!resultado && (
                <button onClick={aplicar} disabled={loading} className="btn-primary w-full">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Aplicar baixas (só sem divergência)
                </button>
              )}
              {resultado && (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-emerald-50 rounded p-2">
                      <div className="text-2xl font-bold text-emerald-700">{resultado.aplicadas}</div>
                      <div className="text-[11px] text-emerald-900">aplicadas</div>
                    </div>
                    <div className="bg-amber-50 rounded p-2">
                      <div className="text-2xl font-bold text-amber-700">{resultado.erros}</div>
                      <div className="text-[11px] text-amber-900">divergências</div>
                    </div>
                    <div className="bg-ink-50 rounded p-2">
                      <div className="text-2xl font-bold text-ink-700">{resultado.total_linhas}</div>
                      <div className="text-[11px] text-ink-900">total</div>
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-auto border rounded">
                    <table className="text-xs w-full">
                      <thead className="bg-ink-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">#</th>
                          <th className="text-left p-2">Identificador</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-right p-2">Planilha</th>
                          <th className="text-right p-2">Esperado</th>
                          <th className="text-left p-2">Detalhe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultado.resultados.map((r: any) => (
                          <tr key={r.linha} className={r.ok ? 'bg-emerald-50/50' : 'bg-amber-50/50'}>
                            <td className="p-2">{r.linha}</td>
                            <td className="p-2 font-mono">{r.identificador}</td>
                            <td className="p-2">
                              {r.ok ? <span className="text-emerald-700 font-semibold">OK</span>
                                : <span className="text-amber-700 font-semibold">DIVERG.</span>}
                            </td>
                            <td className="p-2 text-right font-mono">{r.valor_planilha != null ? formatMoney(r.valor_planilha) : (r.valor_recebido != null ? formatMoney(r.valor_recebido) : '-')}</td>
                            <td className="p-2 text-right font-mono">{r.valor_esperado != null ? formatMoney(r.valor_esperado) : '-'}</td>
                            <td className="p-2">{r.descricao || r.erro}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-4 border-t flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Fechar</button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// MODAL: GERAR BORDERÔ (contas a pagar com aprovação BPM)
// ============================================================
function BorderoModal({ ids, contas, onClose, onDone }: any) {
  const [contaBancariaId, setContaBancariaId] = useState<number | ''>('')
  const [forma, setForma] = useState('pix')
  const [dataPrevista, setDataPrevista] = useState('')
  const [descricao, setDescricao] = useState('')
  const [observacao, setObservacao] = useState('')
  const [criando, setCriando] = useState(false)
  // Valor por título (default = saldo cheio); pode reduzir qualquer um → resto fica em aberto.
  const saldoDe = (c: any) => Math.max(0, Number(c.valor) - Number(c.valor_pago || 0))
  const [valores, setValores] = useState<Record<number, string>>(() => {
    const m: Record<number, string> = {}
    contas.forEach((c: any) => { m[c.id] = saldoDe(c).toFixed(2) })
    return m
  })
  const resetSaldoCheio = () => {
    const m: Record<number, string> = {}
    contas.forEach((c: any) => { m[c.id] = saldoDe(c).toFixed(2) })
    setValores(m)
  }

  const { data: contasBancarias = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  const total = contas.reduce((a: number, c: any) =>
    a + Math.min(saldoDe(c), parseFloat(valores[c.id] || '0') || 0), 0)

  async function executar() {
    if (!contaBancariaId) {
      toast.error('Selecione o banco pagador')
      return
    }
    if (total <= 0) {
      toast.error('Informe ao menos um valor a pagar')
      return
    }
    setCriando(true)
    try {
      const valoresPayload: Record<number, number> = {}
      contas.forEach((c: any) => {
        valoresPayload[c.id] = Math.min(saldoDe(c), parseFloat(valores[c.id] || '0') || 0)
      })
      const r = await api.post('/borderos', {
        descricao: descricao || undefined,
        conta_bancaria_id: contaBancariaId,
        forma_pagamento: forma,
        data_prevista: dataPrevista || undefined,
        contas_ids: ids,
        valores: valoresPayload,
        observacao: observacao || undefined,
      })
      toast.success(`Borderô #${r.data.numero} criado — aguardando aprovação`)
      // Abre o detalhe direto
      window.location.href = `/financeiro/borderos/${r.data.id}`
      onDone()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao criar borderô')
    } finally {
      setCriando(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold">Gerar borderô — {ids.length} conta(s)</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs">
            <div>Total a pagar: <strong className="font-mono">{formatMoney(total)}</strong></div>
            <div className="text-ink-600 mt-1">
              Vai criar borderô numerado + abrir tarefa no BPM pra gestor aprovar.
              Após aprovado, você marca como pago e o sistema baixa as contas + cria lançamentos.
              Edite o valor de cada título — o que sobrar fica em aberto.
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">Valor por título</label>
              <button type="button" onClick={resetSaldoCheio}
                className="text-xs text-blue-600 hover:underline">↺ saldo cheio em todos</button>
            </div>
            <div className="border border-ink-200 rounded max-h-56 overflow-auto divide-y divide-ink-100">
              {contas.map((c: any) => {
                const saldo = saldoDe(c)
                const v = Math.min(saldo, parseFloat(valores[c.id] || '0') || 0)
                const parcial = v > 0 && v < saldo
                return (
                  <div key={c.id} className="flex items-center gap-2 px-2 py-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{c.descricao || '#' + c.id}</div>
                      <div className="text-[10px] text-ink-500 truncate">
                        {c.cliente_codigo ? `[${c.cliente_codigo}] ` : ''}{c.cliente_nome || ''}
                        <span className="ml-1">· saldo {formatMoney(saldo)}{parcial ? ` · resta ${formatMoney(saldo - v)}` : ''}</span>
                      </div>
                    </div>
                    <input type="number" step="0.01" min="0" max={saldo}
                      className={`input w-28 text-right text-sm py-1 ${parcial ? 'border-amber-400' : ''}`}
                      value={valores[c.id] ?? ''}
                      onChange={e => setValores(m => ({ ...m, [c.id]: e.target.value }))} />
                  </div>
                )
              })}
            </div>
          </div>
          <div>
            <label className="label">Banco pagador *</label>
            <select className="input" value={contaBancariaId}
              onChange={e => setContaBancariaId(Number(e.target.value) || '')}>
              <option value="">Selecione...</option>
              {contasBancarias.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Forma</label>
              <select className="input" value={forma} onChange={e => setForma(e.target.value)}>
                <option value="pix">PIX</option>
                <option value="ted">TED/DOC</option>
                <option value="boleto">Boleto</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="cartao_credito">Cartão Crédito</option>
              </select>
            </div>
            <div>
              <label className="label">Data prevista</label>
              <input type="date" className="input" value={dataPrevista}
                onChange={e => setDataPrevista(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">Descrição</label>
            <input className="input" value={descricao}
              placeholder={`Borderô ${new Date().toLocaleDateString('pt-BR')}`}
              onChange={e => setDescricao(e.target.value)} />
          </div>
          <div>
            <label className="label">Observação (opcional)</label>
            <textarea className="input min-h-[60px]" value={observacao}
              onChange={e => setObservacao(e.target.value)} />
          </div>
        </div>
        <div className="px-5 py-4 border-t flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={executar} disabled={criando || !contaBancariaId || total <= 0}
            className="btn-primary bg-emerald-600 hover:bg-emerald-700 flex-1">
            {criando ? <Loader2 className="animate-spin inline mr-1" size={14} /> : null}
            Gerar borderô · {formatMoney(total)}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// MODAL: BAIXAR EM LOTE (contas a pagar)
// ============================================================
function BaixaLoteModal({ ids, contas, onClose, onDone }: any) {
  const [forma, setForma] = useState('pix')
  const [contaBancariaId, setContaBancariaId] = useState<number | ''>('')
  const [data, setData] = useState(new Date().toISOString().split('T')[0])
  const [observacao, setObservacao] = useState('')
  const [executando, setExecutando] = useState(false)
  const [resultado, setResultado] = useState<any>(null)
  // 1 conta só ("baixar direto" de um título) → deixa digitar o valor (permite parcial).
  // Várias contas → lote: quita o saldo cheio de cada (sem parcial).
  const unica = contas.length === 1
  const saldoUnico = unica ? Math.max(0, Number(contas[0].valor) - Number(contas[0].valor_pago || 0)) : 0
  const [valorUnico, setValorUnico] = useState(unica ? saldoUnico.toFixed(2) : '')
  // Lote: valor por título (default = saldo cheio); pode reduzir qualquer um → o resto fica em aberto.
  const saldoDe = (c: any) => Math.max(0, Number(c.valor) - Number(c.valor_pago || 0))
  const [valores, setValores] = useState<Record<number, string>>(() => {
    const m: Record<number, string> = {}
    contas.forEach((c: any) => { m[c.id] = saldoDe(c).toFixed(2) })
    return m
  })
  const resetSaldoCheio = () => {
    const m: Record<number, string> = {}
    contas.forEach((c: any) => { m[c.id] = saldoDe(c).toFixed(2) })
    setValores(m)
  }

  const { data: contasBancarias = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  const total = unica
    ? Math.min(saldoUnico, parseFloat(valorUnico || '0') || 0)
    : contas.reduce((a: number, c: any) => a + Math.min(saldoDe(c), parseFloat(valores[c.id] || '0') || 0), 0)

  async function executar() {
    if (!contaBancariaId) {
      toast.error('Selecione a conta bancária')
      return
    }
    if (total <= 0) {
      toast.error('Informe ao menos um valor a pagar')
      return
    }
    setExecutando(true)
    const resultados: any[] = []
    for (const c of contas) {
      const saldo = Math.max(0, Number(c.valor) - Number(c.valor_pago || 0))
      if (saldo <= 0) {
        resultados.push({ id: c.id, ok: true, motivo: 'já quitada' })
        continue
      }
      const vpago = unica
        ? Math.min(saldo, parseFloat(valorUnico || '0') || 0)
        : Math.min(saldo, parseFloat(valores[c.id] || '0') || 0)
      if (vpago <= 0) {
        resultados.push({ id: c.id, ok: true, motivo: 'pulada (R$ 0)' })
        continue
      }
      try {
        await api.post(`/financeiro/${c.id}/baixar`, {
          valor_pago: vpago,
          data_pagamento: data,
          forma_pagamento: forma,
          conta_id: contaBancariaId,
          observacao: observacao || undefined,
        })
        resultados.push({ id: c.id, ok: true, descricao: c.descricao,
          motivo: vpago < saldo ? `parcial — resta ${formatMoney(saldo - vpago)}` : undefined })
      } catch (e: any) {
        resultados.push({ id: c.id, ok: false, erro: e?.response?.data?.detail || 'erro', descricao: c.descricao })
      }
    }
    setExecutando(false)
    setResultado(resultados)
    const ok = resultados.filter(r => r.ok).length
    toast.success(`${ok}/${resultados.length} baixadas com sucesso`)
    if (ok === resultados.length) {
      setTimeout(onDone, 1500)
    }
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold">{unica ? 'Baixar conta' : `Baixar ${ids.length} contas em lote`}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
            <div>Total a baixar: <strong className="font-mono">{formatMoney(total)}</strong></div>
            <div className="text-ink-600 mt-1">
              {unica ? 'Pode pagar parcial — o saldo restante fica em aberto.'
                     : 'Edite o valor de cada título — o que sobrar fica em aberto. Cada um vira um lançamento individual.'}
            </div>
          </div>
          {!resultado && <>
            {unica && (
              <div>
                <label className="label">Valor a pagar</label>
                <input type="number" step="0.01" min="0" max={saldoUnico} className="input text-lg font-bold"
                  value={valorUnico} onChange={e => setValorUnico(e.target.value)} />
                <p className="text-xs text-ink-500 mt-1">Saldo devedor: {formatMoney(saldoUnico)}</p>
              </div>
            )}
            {!unica && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label mb-0">Valor por título</label>
                  <button type="button" onClick={resetSaldoCheio}
                    className="text-xs text-blue-600 hover:underline">↺ saldo cheio em todos</button>
                </div>
                <div className="border border-ink-200 rounded max-h-56 overflow-auto divide-y divide-ink-100">
                  {contas.map((c: any) => {
                    const saldo = saldoDe(c)
                    const v = Math.min(saldo, parseFloat(valores[c.id] || '0') || 0)
                    const parcial = v > 0 && v < saldo
                    return (
                      <div key={c.id} className="flex items-center gap-2 px-2 py-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{c.descricao || '#' + c.id}</div>
                          <div className="text-[10px] text-ink-500 truncate">
                            {c.cliente_codigo ? `[${c.cliente_codigo}] ` : ''}{c.cliente_nome || ''}
                            <span className="ml-1">· saldo {formatMoney(saldo)}{parcial ? ` · resta ${formatMoney(saldo - v)}` : ''}</span>
                          </div>
                        </div>
                        <input type="number" step="0.01" min="0" max={saldo}
                          className={`input w-28 text-right text-sm py-1 ${parcial ? 'border-amber-400' : ''}`}
                          value={valores[c.id] ?? ''}
                          onChange={e => setValores(m => ({ ...m, [c.id]: e.target.value }))} />
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            <div>
              <label className="label">Conta bancária</label>
              <select className="input" value={contaBancariaId}
                onChange={e => setContaBancariaId(Number(e.target.value) || '')}>
                <option value="">Selecione...</option>
                {contasBancarias.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">Forma de pagamento</label>
                <select className="input" value={forma} onChange={e => setForma(e.target.value)}>
                  <option value="pix">Pix</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="boleto">Boleto</option>
                  <option value="ted">TED/DOC</option>
                  <option value="cartao_credito">Cartão Crédito</option>
                  <option value="cartao_debito">Cartão Débito</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
              <div>
                <label className="label">Data pagamento</label>
                <input type="date" className="input" value={data} onChange={e => setData(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="label">Observação (opcional)</label>
              <input className="input" value={observacao} onChange={e => setObservacao(e.target.value)} />
            </div>
          </>}
          {resultado && (
            <div className="max-h-72 overflow-auto border border-ink-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-ink-50 sticky top-0">
                  <tr><th className="text-left p-2">Status</th><th className="text-left p-2">Descrição</th><th className="text-left p-2">Detalhes</th></tr>
                </thead>
                <tbody>
                  {resultado.map((r: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">{r.ok ? <CheckCircle2 size={13} className="text-emerald-600" /> : <X size={13} className="text-red-600" />}</td>
                      <td className="p-2 truncate max-w-xs">{r.descricao || '#' + r.id}</td>
                      <td className="p-2 text-[10px] text-ink-600">{r.erro || r.motivo || 'baixada'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t flex gap-2">
          {!resultado && <>
            <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
            <button onClick={executar} disabled={executando || !contaBancariaId || total <= 0}
              className="btn-primary flex-1">
              {executando ? <Loader2 className="animate-spin inline mr-1" size={14} /> : null}
              {unica ? 'Baixar conta' : `Baixar lote · ${formatMoney(total)}`}
            </button>
          </>}
          {resultado && <button onClick={onDone} className="btn-primary flex-1">OK</button>}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// COMBO COM BUSCA: escolher pessoa (cliente/fornecedor) digitando o nome ou código
// ============================================================
function PessoaCombo({ pessoas, value, onChange, placeholder }: {
  pessoas: any[]
  value: number | '' | null
  onChange: (id: number | '') => void
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false); setQuery('')
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const selecionada = pessoas.find((p: any) => p.id === value)
  const label = selecionada
    ? `${selecionada.codigo ? '[' + selecionada.codigo + '] ' : ''}${selecionada.nome}`
    : ''
  const q = query.trim().toLowerCase()
  const filtradas = (q
    ? pessoas.filter((p: any) =>
        (p.nome || '').toLowerCase().includes(q) ||
        String(p.codigo || '').toLowerCase().includes(q))
    : pessoas
  ).slice(0, 50)

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <input
          className="input pr-14"
          placeholder={placeholder || 'Digite pra buscar...'}
          value={open ? query : label}
          onChange={e => { setQuery(e.target.value); if (!open) setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQuery('') } }}
        />
        {value ? (
          <button type="button" title="Limpar"
            onClick={() => { onChange(''); setQuery(''); setOpen(false) }}
            className="absolute right-7 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
            <X size={14} />
          </button>
        ) : null}
        <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-ink-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          <button type="button"
            onClick={() => { onChange(''); setOpen(false); setQuery('') }}
            className="w-full text-left px-3 py-2 text-xs text-ink-500 hover:bg-ink-50">— nenhum —</button>
          {filtradas.length === 0 && <div className="px-3 py-2 text-xs text-ink-400">Nada encontrado</div>}
          {filtradas.map((p: any) => (
            <button key={p.id} type="button"
              onClick={() => { onChange(p.id); setOpen(false); setQuery('') }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${p.id === value ? 'bg-blue-50 font-medium' : ''}`}>
              {p.codigo ? <span className="text-ink-400">[{p.codigo}] </span> : null}{p.nome}
            </button>
          ))}
          {!q && pessoas.length > 50 && (
            <div className="px-3 py-1.5 text-[10px] text-ink-400 border-t">Mostrando 50 — digite pra filtrar o restante</div>
          )}
        </div>
      )}
    </div>
  )
}

// Combo de busca digitada pra CONTA GERENCIAL (mesma mecânica do PessoaCombo).
// Resolve a dor de ter que rolar a lista enorme — digita código ou nome e filtra.
function ContaGerencialCombo({ items, value, onChange, placeholder }: {
  items: any[]
  value: number | '' | null
  onChange: (id: number | '') => void
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setQuery('') }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const sel = items.find((c: any) => c.id === value)
  const label = sel ? `${sel.codigo} — ${sel.nome}` : ''
  const q = query.trim().toLowerCase()
  const filtradas = (q
    ? items.filter((c: any) =>
        (c.nome || '').toLowerCase().includes(q) ||
        String(c.codigo || '').toLowerCase().includes(q))
    : items
  ).slice(0, 50)

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <input
          className="input pr-14"
          placeholder={placeholder || 'Digite código ou nome...'}
          value={open ? query : label}
          onChange={e => { setQuery(e.target.value); if (!open) setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQuery('') } }}
        />
        {value ? (
          <button type="button" title="Limpar"
            onClick={() => { onChange(''); setQuery(''); setOpen(false) }}
            className="absolute right-7 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
            <X size={14} />
          </button>
        ) : null}
        <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-ink-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          <button type="button"
            onClick={() => { onChange(''); setOpen(false); setQuery('') }}
            className="w-full text-left px-3 py-2 text-xs text-ink-500 hover:bg-ink-50">— sem classificação —</button>
          {filtradas.length === 0 && <div className="px-3 py-2 text-xs text-ink-400">Nada encontrado</div>}
          {filtradas.map((c: any) => (
            <button key={c.id} type="button"
              onClick={() => { onChange(c.id); setOpen(false); setQuery('') }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${c.id === value ? 'bg-blue-50 font-medium' : ''}`}>
              <span className="text-ink-400">{c.codigo}</span> — {c.nome}
            </button>
          ))}
          {!q && items.length > 50 && (
            <div className="px-3 py-1.5 text-[10px] text-ink-400 border-t">Mostrando 50 — digite pra filtrar o restante</div>
          )}
        </div>
      )}
    </div>
  )
}

// Classificação em 2 níveis do Plano de Contas: Grupo macro (nível 1) → Subgrupo (analítica, nível 2).
// O valor salvo continua sendo o id da analítica (subgrupo). `items` vêm de /plano-contas/analiticas
// já com macro_id/macro_nome/macro_codigo.
function ContaGerencialCascata({ items, value, onChange, placeholder }: {
  items: any[]
  value: number | '' | null
  onChange: (id: number | '') => void
  placeholder?: string
}) {
  // grupos macro únicos (na ordem do código)
  const macros = useMemo(() => {
    const m = new Map<number, { id: number; codigo: string; nome: string }>()
    items.forEach((c: any) => {
      if (c.macro_id != null && !m.has(c.macro_id))
        m.set(c.macro_id, { id: c.macro_id, codigo: c.macro_codigo, nome: c.macro_nome })
    })
    return Array.from(m.values()).sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }))
  }, [items])

  // macro deduzido a partir do subgrupo selecionado (ao editar um título existente)
  const selecionada = items.find((c: any) => c.id === value)
  const [macroSel, setMacroSel] = useState<number | ''>(selecionada?.macro_id ?? '')
  useEffect(() => { setMacroSel(selecionada?.macro_id ?? '') }, [selecionada?.macro_id])

  const subgrupos = useMemo(
    () => items.filter((c: any) => macroSel !== '' && c.macro_id === Number(macroSel))
      .sort((a: any, b: any) => String(a.codigo).localeCompare(String(b.codigo), undefined, { numeric: true })),
    [items, macroSel])

  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        className="input"
        value={macroSel}
        onChange={(e) => {
          const v = e.target.value ? Number(e.target.value) : ''
          setMacroSel(v)
          onChange('')  // troca de macro zera o subgrupo
        }}>
        <option value="">{placeholder || 'Grupo (macro)…'}</option>
        {macros.map((m) => (
          <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>
        ))}
      </select>
      <select
        className="input"
        value={value ?? ''}
        disabled={macroSel === ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}>
        <option value="">{macroSel === '' ? 'Escolha o grupo antes' : 'Subgrupo…'}</option>
        {subgrupos.map((c: any) => (
          <option key={c.id} value={c.id}>{c.codigo} — {c.nome}</option>
        ))}
      </select>
    </div>
  )
}

function ImpressoesReceberMenu({ vendedores = [] }: { vendedores?: Array<{ id: number; codigo?: string | null; nome?: string | null }> }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  // Submenu de escolha de vendedor (quando o relatório é "por vendedor")
  const [escolhaVend, setEscolhaVend] = useState<null | { agrupar: string; filtro: string; erro: string; label: string }>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  const fechar = () => { setPos(null); setEscolhaVend(null) }
  const toggle = () => {
    if (pos) { fechar(); return }
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 240) })
  }

  const abrir = (agrupar: string, filtro: string, erroLabel: string, vendedorId?: number) => {
    fechar()
    const token = localStorage.getItem('pain_token')
    const t = toast.loading('Gerando PDF...')
    let url = `/api/financeiro/receber-pdf?agrupar=${agrupar}&filtro=${filtro}`
    if (vendedorId) url += `&vendedor_id=${vendedorId}`
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob() })
      .then((blob) => {
        toast.dismiss(t)
        const u = URL.createObjectURL(blob)
        window.open(u, '_blank')
        setTimeout(() => URL.revokeObjectURL(u), 60000)
      })
      .catch((e) => { toast.dismiss(t); toast.error(`${erroLabel}: ${e.message}`) })
  }

  // Clicar num item: se for "por vendedor", abre o submenu de escolha; senão gera direto.
  const clicarItem = (agrupar: string, filtro: string, erro: string, label: string) => {
    if (agrupar === 'vendedor') setEscolhaVend({ agrupar, filtro, erro, label })
    else abrir(agrupar, filtro, erro)
  }

  const itens: Array<[string, string, string, string]> = [
    ['Por Cobrança',         'cobranca', 'abertos',  'Falha no por cobrança'],
    ['Por Vendedor',         'vendedor', 'abertos',  'Falha no por vendedor'],
    ['Por Cliente',          'cliente',  'abertos',  'Falha no por cliente'],
    ['Por Aging (em dias)',  'aging',    'abertos',  'Falha no aging'],
    ['Atrasados — Cobrança', 'cobranca', 'atrasados', 'Falha nos atrasados'],
    ['Atrasados — Vendedor', 'vendedor', 'atrasados', 'Falha nos atrasados'],
    ['Atrasados — Cliente',  'cliente',  'atrasados', 'Falha nos atrasados'],
    ['A vencer — Aging',     'aging',    'a_vencer',  'Falha em a vencer'],
  ]

  return (
    <div className="inline-block">
      <button ref={btnRef} onClick={toggle}
        className="btn-secondary flex items-center gap-1">
        <Printer size={14} /> Impressões <ChevronDown size={12} />
      </button>
      {pos && (
        <>
          <div className="fixed inset-0 z-40" onClick={fechar} />
          <div className="fixed z-50 bg-white border border-ink-200 rounded-lg shadow-lg py-1 w-[240px] text-left max-h-[70vh] overflow-auto"
            style={{ top: pos.top, left: pos.left }}>
            {!escolhaVend ? (
              itens.map(([label, agrupar, filtro, erro]) => (
                <button key={`${agrupar}-${filtro}-${label}`}
                  onClick={() => clicarItem(agrupar, filtro, erro, label)}
                  className="w-full text-left px-3 py-2 text-xs text-ink-700 hover:bg-slate-50 flex items-center justify-between">
                  {label}
                  {agrupar === 'vendedor' && <ChevronDown size={11} className="-rotate-90 text-ink-400" />}
                </button>
              ))
            ) : (
              <>
                <button onClick={() => setEscolhaVend(null)}
                  className="w-full text-left px-3 py-2 text-xs font-medium text-ink-500 hover:bg-slate-50 border-b border-ink-100">
                  ← {escolhaVend.label}
                </button>
                <button onClick={() => abrir(escolhaVend.agrupar, escolhaVend.filtro, escolhaVend.erro)}
                  className="w-full text-left px-3 py-2 text-xs font-medium text-brand-700 hover:bg-slate-50">
                  Todos os vendedores
                </button>
                {vendedores.length === 0 && (
                  <div className="px-3 py-2 text-xs text-ink-400 italic">Nenhum vendedor nos títulos.</div>
                )}
                {vendedores.map((v) => (
                  <button key={v.id}
                    onClick={() => abrir(escolhaVend.agrupar, escolhaVend.filtro, escolhaVend.erro, v.id)}
                    className="w-full text-left px-3 py-2 text-xs text-ink-700 hover:bg-slate-50">
                    {v.codigo ? <span className="text-brand-700">#{v.codigo} </span> : ''}{v.nome}
                  </button>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
